/**
 * `createProDocClient()` — the typed client over `prodoc-service`, and the
 * **Storage** capability's package surface (ADR-0010). The other three
 * capabilities' components (`<ProDocUploader>`, `<ProDocLibrary>`,
 * `<ProDocReview>`) are built on it and on nothing else.
 *
 * **It imports nothing from the app**: no session store, no org store, no
 * router, no config. The host hands it three things — an authenticated
 * `fetcher` (its own session store's `authed`, which owns the bearer, the
 * serialized refresh and what a dead session means), the selected org and the
 * service's base URL. That is what lets a sibling app render ProDoc's screens
 * inside its own shell under its own session, with never a second watchdog on
 * the page (ADR-0007 §Consequences). `tests/package-boundary.test.ts` pins it.
 *
 * **The contract it speaks is `prodoc-service`'s at W5** (plan §5): routes
 * under `/api/v1`, `X-Fiveox-Org` on every call, the constitutional envelope
 * `{success, data, meta}` / `{success:false, error:{code, message, details}}`,
 * the closed code set with a machine `details.reason` (D13), the **record of
 * evidence** — a document with its title, type, versions, read log and audit
 * trail, and nothing a parser would fill (ADR-0008) — its **review state**,
 * moved only by a person's request, approval or rejection, or by a new version
 * (D14, FR-22) — and, from W5, where it is **organised**: its folder (D16), its
 * tags (D17), the documents related to it, the register's every filter (D18),
 * bulk actions with per-document answers, the streamed ZIP's ticket (D20) and
 * the dashboard's summary (FR-31).
 *
 * **W6 adds how long it is kept and who else may read it**: its retention —
 * the statutory floor with its citation, the organisation's rule above it, the
 * override behind step-up (D22, D24) — the tombstone an expired document
 * leaves (FR-26), the DPDP erasure request (D25), the shares that widen sight
 * to members, a link or a sibling app (D26), the send through comm as the
 * organisation (D27), and the settings sections for both. Step-up travels as
 * `X-StepUp-Assertion`, minted by the host for the one request and never kept
 * here. **A share link's reader** has a client of their own,
 * `createShareLinkClient()`: no organisation header, the token and the
 * passcode as headers (`X-Share-Token`, `X-Share-Passcode`), never in a path
 * or a query (D26, D35).
 * `tests/prodoc-client.test.ts` pins every shape below.
 *
 * **Engine W15 adds `parsing`** — what a person, or a sibling's screen on a
 * person's session, asks of the engine (FR-E3, FR-E9, FR-E13, FR-E20, FR-E21):
 * the org's profiles, a parse request and its job, a document's extractions,
 * one extraction with its fields once its review allows, corrections, accept
 * and reject, and the review queue. The shapes are `extraction-shapes.ts`'s.
 * **Engine W16 adds a version's page as the engine draws it** (C-E12, D44) — a
 * PNG for a TIFF, which no browser but Safari draws, through a logged read.
 *
 * **W23 adds personal data** (FR-48 – FR-53, C-W23): a document's
 * `personal_data` summary and the register's `personal_data=` filter, a
 * version's findings — kind, **masked form**, location, never the value — and
 * the two reveals, one value each, behind `personal_data:can_view`, logged by
 * the service before it answers and asked for `no-store`. The shapes are
 * `personal-data.ts`'s; a person's extraction read says which of its fields the
 * service masked (`meta.masked_paths`).
 */

import {
  MAX_MAP_NAME,
  MAX_MAP_SCOPE,
  MAX_ROWS_PAGE,
  columnMapOf,
  correctionAnswerOf,
  extractionOf,
  extractionRecordOf,
  extractionRowsOf,
  jobOf,
  mapPreviewAnswerOf,
  mapSpecBody,
  surveyAnswerOf,
  parseAnswerOf,
  parseBody,
  passwordStateOf,
  profileListOf,
  queueItemOf,
  readingOf,
  type ColumnMap,
  type ColumnMapInput,
  type ColumnMapQuery,
  type ColumnMapVersionInput,
  type CorrectionAnswer,
  type DocumentReading,
  type ExtractionRows,
  type ExtractionRowsQuery,
  type MapPreviewAnswer,
  type MapPreviewInput,
  type SurveyAnswer,
  type SurveyInput,
  type PasswordState,
  type ReadingState,
  type CorrectionsInput,
  type Extraction,
  type ExtractionDecisionInput,
  type ExtractionQuery,
  type ExtractionQueueItem,
  type ExtractionRecord,
  type ParseAnswer,
  type ParseInput,
  type ParseJob,
  type ParseOnUpload,
  type ProfileList,
} from './extraction-shapes'
import {
  splitFromOf,
  splitPagesOf,
  splitPartsOf,
  splitStateOf,
  type SplitFrom,
  type SplitPages,
  type SplitPart,
  type SplitState,
} from './split-shapes'
import {
  findingsOf,
  personalDataOf,
  revealedFieldOf,
  revealedValueOf,
  type DocumentPersonalData,
  type PersonalDataFilter,
  type PersonalDataFindings,
  type RevealedField,
} from './personal-data'

export type Fetcher = (url: string, init?: RequestInit) => Promise<Response>

/* ------------------------------------------------------------------ errors */

/**
 * A refusal with the machine code the service sent.
 *
 * The code is the contract: a screen switches on `USAGE_LIMIT_EXCEEDED` to say
 * "the storage limit is reached" and on `NOT_ENTITLED` to render the paywall.
 * Throwing a bare `Error` here is what produces "something went wrong".
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /**
   * True when the refusal is about entitlement rather than about this user.
   * Never for a sibling's pair (`details.integration`, FR-33): that is a
   * connection an administrator enables, not a plan the store sells.
   */
  get isPaywall(): boolean {
    if (typeof this.details.integration === 'string') return false
    return this.code === 'NOT_ENTITLED' || this.code === 'USAGE_LIMIT_EXCEEDED'
  }

  /**
   * Why. A member's refusal carries `details.entitlement_reason`; a sibling's
   * refused integration pair carries `details.reason` beside
   * `details.integration` (FR-23a) — both are the platform's own words.
   */
  get entitlementReason(): string | undefined {
    const reason = this.details.entitlement_reason ?? this.details.reason
    return typeof reason === 'string' ? reason : undefined
  }

  /**
   * The domain refusal's machine reason (plan §5.1, D13): `duplicate`,
   * `corrupt`, `unsupported_type`, `too_large`, `too_many_files`,
   * `same_as_current`, `version_conflict`, `default_workspace`, … — what a
   * screen switches on beside the generic code.
   */
  get reason(): string | undefined {
    const reason = this.details.reason
    return typeof reason === 'string' ? reason : undefined
  }
}

/**
 * The closed set the service answers with (plan §2), and each code's status.
 * The domain half grows it by exactly the codes the spec names (D13):
 * `FOUR_EYES` in W4 — the approver uploaded the version under review and the
 * organisation's four-eyes switch is on (FR-23, "`FORBIDDEN` with a machine
 * code"), and `APP_OWNED` in W5 — a sibling app's folder, whose structure
 * that app keeps (§9-19): people file into it, never rename, move or archive it.
 */
export const ERROR_STATUS = {
  VALIDATION_ERROR: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL: 500,
  NOT_ENTITLED: 403,
  USAGE_LIMIT_EXCEEDED: 409,
  STEPUP_REQUIRED: 403,
  STEPUP_INVALID: 403,
  STORAGE_UNAVAILABLE: 503,
  FOUR_EYES: 403,
  APP_OWNED: 409,
  // Engine W12 (`specs/engine/plan.md` §5): an archived or expired document is
  // not parsed, a profile that cannot read the version's media type, and an
  // extraction's fields asked for before its review allows them (FR-E8, FR-E19).
  DOCUMENT_NOT_LIVE: 409,
  PROFILE_MEDIA_MISMATCH: 400,
  EXTRACTION_NOT_READY: 409,
} as const

export type ErrorCode = keyof typeof ERROR_STATUS

/**
 * The `details.reason` values the routes answer with so far (plan §5.1): W3's,
 * W4's `illegal_transition`, and W5's — a folder's `not_empty`, `cycle`,
 * `too_deep`, `name_taken` and `cross_workspace`, and the ZIP's `zip_too_large`
 * and `ticket_used`.
 */
export type RefusalReason =
  | 'duplicate'
  | 'same_as_current'
  | 'unsupported_type'
  | 'corrupt'
  | 'too_large'
  | 'too_many_files'
  | 'version_conflict'
  | 'default_workspace'
  | 'archived'
  | 'illegal_transition'
  | 'not_empty'
  | 'cycle'
  | 'too_deep'
  | 'name_taken'
  | 'cross_workspace'
  | 'zip_too_large'
  | 'ticket_used'
  // W6 (plan §5.1): a tombstone, the floor, and a share link's refusals.
  | 'expired'
  | 'below_floor'
  | 'passcode_required'
  | 'passcode_wrong'
  | 'locked'
  | 'sign_in_required'
  // W23 (FR-52): a reveal whose location no longer masks to the finding's masked form.
  | 'mismatch'

/**
 * Step-up was asked for (`STEPUP_REQUIRED`) or the assertion sent did not
 * hold (`STEPUP_INVALID`, expired or another app's): the screen launches the
 * challenge rather than showing a failure (`app/platform/stepup.py`).
 */
export function isStepUpRequired(e: unknown): boolean {
  return e instanceof ApiError && (e.code === 'STEPUP_REQUIRED' || e.code === 'STEPUP_INVALID')
}

/**
 * What the host asks core-auth for (T6.25's `StepUpDialog`): a person
 * re-verifies, and the host hands back the assertion — for the **one** request
 * it was minted for. It rejects with `StepUpCancelled` when they close it.
 * The title and the sentence name the irreversible thing, because "confirm it
 * is you" with no object is a prompt people learn to click through.
 */
export type StepUpAsk = (request: { title: string; what: string }) => Promise<string>

/** The person closed the re-verification: nothing was sent, and nothing is said to have failed. */
export class StepUpCancelled extends Error {
  constructor() {
    super('Re-verification was cancelled.')
    this.name = 'StepUpCancelled'
  }
}

export const isStepUpCancelled = (e: unknown): boolean => e instanceof StepUpCancelled

/**
 * Run an action that may need step-up. `ask` first when the action always
 * needs it (`upfront`) — an `anyone` link, an override, an erasure — else try
 * without and ask only when the service says `STEPUP_REQUIRED`; an assertion
 * the service finds stale (`STEPUP_INVALID`) is asked for once more. The
 * assertion lives only in this call: it is never stored, and a refusal before
 * the write is all a missing one costs.
 */
export async function withStepUp<T>(
  run: (assertion: string | undefined) => Promise<T>,
  ask: StepUpAsk | undefined,
  request: { title: string; what: string },
  upfront = false,
): Promise<T> {
  if (!ask) return run(undefined)
  let assertion = upfront ? await ask(request) : undefined
  for (let attempt = 0; ; attempt++) {
    try {
      return await run(assertion)
    } catch (e) {
      if (!isStepUpRequired(e) || attempt >= 1) throw e
      assertion = await ask(request)
    }
  }
}

/**
 * A rule or an override below the statute (`VALIDATION_ERROR`, `below_floor`,
 * D22): the floor the service named and its citation, so the screen says it at
 * the field. Every name the details may carry it under is read.
 */
export interface BelowFloor {
  /** Which field the service refused: `retention` (a rule) or `retain_until` (an override). */
  field: string | null
  /** The document type whose floor it is, when the refusal names one. */
  document_type: string | null
  /** The rule's owning app, when the refused rule named one. */
  owner_app: string | null
  /** The rule's place in the submitted list, when the refusal names one. */
  index: number | null
  /** The floor, in months after the anchor. */
  months: number | null
  /** The floor as a date, for a single document's override. */
  until: string | null
  citation: string | null
}

export function belowFloorOf(e: unknown): BelowFloor | null {
  if (!(e instanceof ApiError) || e.reason !== 'below_floor') return null
  const d = e.details
  const floor = asRecordOf(d.floor) ?? {}
  const str = (...values: unknown[]) => values.find((v): v is string => typeof v === 'string' && v.length > 0) ?? null
  const num = (...values: unknown[]) => values.find((v): v is number => typeof v === 'number' && Number.isFinite(v)) ?? null
  // The service names every floor that binds, each with its citation (`details.floors`).
  const listed = [...(Array.isArray(d.floors) ? d.floors : []), ...(Array.isArray(d.citations) ? d.citations : [])]
  const citations = listed.map((c) => (typeof c === 'string' ? c : str(asRecordOf(c)?.citation))).filter((c): c is string => Boolean(c))
  return {
    field: str(d.field),
    document_type: str(d.document_type, floor.document_type),
    owner_app: str(d.owner_app),
    index: num(d.index, d.rule_index),
    // `months` on a rule refusal is the months the rule *asked for*; the floor is `minimum_months`.
    months: num(d.minimum_months, d.floor_months, floor.months),
    until: str(d.floor_until, d.until, floor.until),
    citation: str(d.citation, floor.citation, [...new Set(citations)].join('; ')),
  }
}

/** A tombstone's `NOT_FOUND` (FR-26): the record is kept, its bytes are gone — when, and why. */
export function expiredOf(e: unknown): { expired_at: string | null } | null {
  if (!(e instanceof ApiError) || e.code !== 'NOT_FOUND' || e.reason !== 'expired') return null
  const at = e.details.expired_at
  return { expired_at: typeof at === 'string' ? at : null }
}

/**
 * A sibling app's pair refused the call (FR-33, plan D30): the organisation's
 * `prodoc-<slug>` integration is not active, or does not grant the scope the
 * call needs. The service answers **`NOT_FOUND`** with `details.integration`
 * (W1 answered `NOT_ENTITLED`, which read as a paywall) and a `details.reason`
 * — `not_enabled`, `entitlement_lapsed`, `authority_unreachable` or
 * `scope_missing` (with `details.scope`). It is neither a missing document nor
 * the paywall: a host renders it as "not connected", with the reason.
 */
export interface PairRefusal {
  /** The pair's code, e.g. `prodoc-gst`. */
  integration: string
  /** The sibling's slug, from the code — `gst`. */
  app: string
  /** The platform's reason, as it said it. */
  reason: string
  /** With `scope_missing`: the scope the call needed, `document.read` or `document.write`. */
  scope?: string
}

export function pairRefusalOf(e: unknown): PairRefusal | null {
  if (!(e instanceof ApiError) || e.code !== 'NOT_FOUND') return null
  const integration = e.details.integration
  if (typeof integration !== 'string' || !integration) return null
  const scope = e.details.scope
  return {
    integration,
    app: integration.replace(/^prodoc-/, ''),
    reason: e.reason ?? 'not_enabled',
    ...(typeof scope === 'string' ? { scope } : {}),
  }
}

export function isPairRefusal(e: unknown): e is ApiError {
  return pairRefusalOf(e) !== null
}

/**
 * A share link's refusal, as what the reader must do next (plan §5.3): give a
 * passcode, try it again, wait out the lockout, sign in — or nothing, because
 * the link is unknown, expired or revoked (one `NOT_FOUND` for all three).
 */
export type LinkRefusal = 'passcode_required' | 'passcode_wrong' | 'locked' | 'sign_in_required' | 'gone' | 'failed'

export function linkRefusalOf(e: unknown): LinkRefusal {
  if (!(e instanceof ApiError)) return 'failed'
  if (e.reason === 'passcode_required' || e.reason === 'passcode_wrong' || e.reason === 'locked' || e.reason === 'sign_in_required') return e.reason
  if (e.code === 'NOT_FOUND') return 'gone'
  return 'failed'
}

/**
 * `asRecord` for the helpers above it in the file — the shaping section's own
 * is declared further down, and a `const` is not hoisted.
 */
function asRecordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

/**
 * A folder whose structure a sibling app keeps (`APP_OWNED`, §9-19): ProDesk's
 * folder per client, say. A person files documents into it; renaming, moving
 * and archiving it happen in the app that made it.
 */
export function isAppOwned(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'APP_OWNED'
}

/**
 * A decision refused by the four-eyes rule (FR-23): the person deciding
 * uploaded the version under review, and the organisation keeps the switch on.
 * Someone else decides it.
 */
export function isFourEyes(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'FOUR_EYES'
}

/**
 * The review moved on before this decision reached it (`CONFLICT`,
 * `illegal_transition`, D14): someone else approved or rejected it first, or a
 * new version re-entered review. The screen re-reads rather than retries.
 */
export function isIllegalTransition(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'CONFLICT' && e.reason === 'illegal_transition'
}

/**
 * W23 (FR-52, D67): a reveal refused because what the file holds at the
 * finding's location no longer masks to the masked form on record (`CONFLICT`,
 * `mismatch`) — nothing was revealed.
 */
export function isRevealMismatch(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'CONFLICT' && e.reason === 'mismatch'
}

/** The ids a duplicate refusal names (D7: `details.duplicate_of`), within the caller's sight. */
export function duplicatesNamed(e: unknown): string[] {
  if (!(e instanceof ApiError) || e.reason !== 'duplicate') return []
  const raw = e.details.duplicate_of
  return Array.isArray(raw) ? raw.filter((d): d is string => typeof d === 'string') : typeof raw === 'string' ? [raw] : []
}

/**
 * Engine W12 (FR-E19): an extraction's fields asked for before its review
 * allows them — `EXTRACTION_NOT_READY` with the state and the reasons, so a
 * reader without `extraction:can_update` is told it waits, and why.
 */
export function notReadyOf(e: unknown): { state: string | null; reasons: string[] } | null {
  if (!(e instanceof ApiError) || e.code !== 'EXTRACTION_NOT_READY') return null
  const reasons = e.details.review_reasons
  return {
    state: typeof e.details.review_state === 'string' ? e.details.review_state : null,
    reasons: Array.isArray(reasons) ? reasons.filter((r): r is string => typeof r === 'string') : [],
  }
}

/* ------------------------------------------------------------- permissions */

/** The five verbs access-management understands. Not extensible by us. */
export type Verb = 'can_view' | 'can_view_all' | 'can_create' | 'can_update' | 'can_delete'
export type Verbs = Partial<Record<Verb, boolean>>

/** The resources `prodoc-service` publishes at boot. Mirrors its `rbac.py` (plan §5.5: six). */
export const DOCUMENT = 'document'
export const WORKSPACE = 'workspace'
export const FOLDER = 'folder'
export const TAG = 'tag'
export const SHARE = 'share'
export const SETTINGS = 'settings'
/**
 * Engine W12 (FR-E39, D18): the seventh resource. `can_create` asks for a
 * parse, `can_view` reads an extraction's fields (the spec's `can_read`), and
 * `can_update` corrects, accepts and rejects.
 */
export const EXTRACTION = 'extraction'
/**
 * W23 (FR-51, FR-52, D65): the eighth resource. Its `can_view` means **may
 * reveal** one masked value — `admin` and `reviewer` by default, never
 * `uploader` or `viewer`. Seeing the masked forms needs only the document's sight.
 */
export const PERSONAL_DATA = 'personal_data'

/**
 * `GET /api/v1/me/access` — the same evaluation object the API enforces with,
 * **plus** the member's visible workspace ids, so the browser filters the way
 * the repository does (plan §2).
 *
 * `visible_workspace_ids` is **`null` for sight of every workspace**
 * (`can_view_all` on `document`) and otherwise the member's workspaces —
 * always including the organisation's default workspace, which every member
 * with `can_view` sees (D9).
 */
export interface AccessAnswer {
  permissions: Record<string, Verbs>
  has_access: boolean
  entitled: boolean
  /** `no_subscription`, `subscription_suspended`, `never_assigned`, … — a support advisor's question. */
  entitlement_reason: string
  known_resources?: string[]
  visible_workspace_ids: string[] | null
}

/* -------------------------------------------------------------- documents */

/**
 * Storage's status (D1): `stored`, `archived`, and from W6 `expired` — a
 * tombstone whose bytes the retention job (or a DPDP erasure) removed, its
 * record and history kept (FR-26). The review state is its own field.
 */
export type DocumentStatus = 'stored' | 'archived' | 'expired'

/**
 * The four-eyes state (FR-20, D1, D14) — its own column, never `status`:
 * `uploaded → needs_review → approved | rejected`. Archiving leaves it as it
 * was, so a restore returns the document to exactly the state it left. There
 * is **no reopen**: a later version is the only way back into review (FR-22).
 */
export type ReviewState = 'uploaded' | 'needs_review' | 'approved' | 'rejected'

/** In the order a document moves through them — the vocabulary when `/options` does not serve one. */
export const REVIEW_STATES: ReviewState[] = ['uploaded', 'needs_review', 'approved', 'rejected']

/** A custom field's value — a scalar (plan §4.2): text, a number (money as integer paise), a yes/no, an ISO date. */
export type CustomValue = string | number | boolean | null

/**
 * The document record (spec FR-8) — what `GET /api/v1/documents/{id}` returns,
 * and one row of the register. The file facts (`file_name`, `media_type`,
 * `size_bytes`, `sha256`) are the **current version's**, mirrored on the row
 * (D2). The object key never reaches the browser: it is a capability, not a
 * field. On a **list** read, identifiers inside the title, notes and custom
 * fields are masked; the single read carries them in full (D19).
 */
export interface ProDocDocument {
  id: string
  org_id: string
  /** Every document is in a workspace — the organisation's default one when none was chosen (D9). */
  workspace_id: string
  /** The slug of the app whose record this is: `prodoc` for an upload here. */
  owner_app: string
  /** The owning app's own key for the record. Opaque; ProDoc never interprets it. */
  source_ref: string | null
  /** What a person calls it; the file name until someone changes it. */
  title: string
  /** A code from the organisation's document-type list (FR-5); `other` by default. Never inferred. */
  document_type: string
  /** The date *on* the document, `YYYY-MM-DD`; the upload date is `created_at`. */
  document_date: string | null
  notes: string | null
  custom_fields: Record<string, CustomValue>
  /** The current version's file name — a fact of the bytes, not editable (plan §5.2). */
  file_name: string
  /** Decided by signature, never by extension (FR-2). */
  media_type: string
  size_bytes: number
  /** Computed by the service as it reads the upload — never trusted from a client. */
  sha256: string
  /** PDF only; null when the file is not a PDF or will not open. */
  page_count: number | null
  /** An encrypted PDF or OOXML package: accepted and marked (FR-4). */
  encrypted: boolean
  current_version_id: string
  version_count: number
  status: DocumentStatus
  /** Who uploaded the **first** version; a later version's uploader is on the version. An app acting for a member (FR-40) names the member here. */
  uploaded_by: string | null
  /**
   * W9 (FR-40): the someone outside the organisation an app stored the first
   * version for — `<kind>:<uuid>`, ProDesk's `person:<id>` for a client's
   * contact. Never a name; `null` when a member or no one was named.
   */
  uploaded_party?: string | null
  /**
   * W4: who uploaded the **current** version — whom four-eyes refuses (FR-23)
   * and whose own upload D12 lets correct. `null` for a sibling app's version
   * (no person uploaded it); absent only from a service older than W4.
   */
  current_version_uploaded_by?: string | null
  created_at: string
  updated_at: string
  archived_at: string | null
  archived_by?: string | null
  /** The single read may carry the current version itself. */
  current_version?: DocumentVersion
  /** The four-eyes state (W4) — never `status`'s concern (D1). Absent only from a service older than W4. */
  review_state?: ReviewState
  /** When it last entered `needs_review` — the review queue's order, oldest first. */
  review_requested_at?: string | null
  /** The last decision: when, by whom, on which version, and what the reviewer said (D14). */
  reviewed_at?: string | null
  reviewed_by?: string | null
  reviewed_version_id?: string | null
  /** The reviewer's comment, up to 1,000 characters — what the uploader reads on a rejection. */
  review_comment?: string | null
  /**
   * W5: the folder it is filed in, of its own workspace (D16) — `null` is the
   * workspace's root. Absent only from a service older than W5.
   */
  folder_id?: string | null
  /** W5: the tags it carries (D17), by id; the names come from `tags.list()`, so a rename touches no document. */
  tag_ids?: string[]
  /** W5, on the single read: the folder's ancestors and itself, from the workspace's root down. */
  breadcrumb?: FolderCrumb[]
  /** W6: the retention class resolved for it (D22) — the document type's, or the org rule's. */
  retention_class?: string | null
  /**
   * W6: the day it may go — `max(floor, org rule)`, or the override (D24).
   * `YYYY-MM-DD`. W9 (FR-25 as amended, D46): `null` for a sibling's document
   * **kept by its app** (`retention_basis.kept_by`) — no date, never flagged,
   * never expired; read it through `keptByOf`.
   */
  retain_until?: string | null
  /** W6: the statute's floor for it, the longest of the floors its type carries (D22). */
  retention_floor_until?: string | null
  /** W6: how `retain_until` was reached — the anchor, every floor with its citation, the org rule. */
  retention_basis?: RetentionBasis | null
  /** W6: an administrator's override, with why, who and when (FR-27). */
  retention_override_until?: string | null
  retention_override_reason?: string | null
  retention_overridden_by?: string | null
  retention_overridden_at?: string | null
  /** W6: a live document past `retain_until` — flagged by the daily job, never removed (FR-26). */
  retention_flagged_at?: string | null
  /** W6: when its bytes were removed, and why (`retention` or `erasure`). */
  expired_at?: string | null
  expiry_reason?: 'retention' | 'erasure' | string | null
  /** W6, on the single read: how many live shares reach it. */
  share_count?: number
  /**
   * W22 (FR-55, FR-57, D52): where the reading of the **current version**
   * stands — the service's, from its latest parse job and that job's review.
   * A later version starts again at `none`. Absent from a service older than W22.
   */
  reading?: DocumentReading | null
  /** W22 (FR-58, D52): whether the current version needs a password, and whether one was given. W30: `saved` — kept sealed (FR-67). */
  password?: PasswordState | null
  /**
   * W30 (FR-67, ADR-0021): the **current version's** password is saved, sealed
   * — used for the reader, never shown. Never the value. Read it through
   * `passwordSavedOf`, which also takes `password: saved` and the current
   * version's own `password_saved`.
   */
  password_saved?: boolean
  /**
   * W23 (FR-50, FR-51, C-W23): the **current version's** personal data — its
   * scan's state, why it was not scanned, and the kinds found, the most
   * sensitive first. Never a value. Absent from a service older than W23.
   */
  personal_data?: DocumentPersonalData | null
  /**
   * W37 (C8, ADR-0025): an original's mark — `split` (cut into parts, kept unchanged,
   * never re-versioned while it is) or `uncertain` (read whole: the engine was not sure
   * where it divides). `null` when never split. Absent from a service older than W37.
   */
  split_state?: SplitState | null
  /** W37: a part's original — the document and the version it was cut from. */
  split_from?: SplitFrom | null
  /** W37: a part's pages in its original — read them through `pagesLabel`. */
  split_pages?: SplitPages | null
  /** W37, on the single read of a split original: its parts in your sight, in page order. */
  split_parts?: SplitPart[]
}

/** One row of the register (C-W22's name): the document record, `reading` and `password` included. */
export type DocumentRow = ProDocDocument

/**
 * W6 (plan §4.2, `retention_basis`): the anchor a floor counts from — the date
 * on the document, else the upload date — the statutory floors its type
 * carries, each with the citation and the day it ends, the org rule if any,
 * and the settings version it was resolved under (so the job can tell when to
 * recompute).
 */
export interface RetentionBasis {
  anchor?: string | null
  anchor_kind?: 'document_date' | 'upload_date' | string | null
  floors?: { rule: string; citation: string; until: string; code?: string; months?: number; verified?: boolean }[]
  /** The floor's day — the statute's, else the organisation minimum's. */
  floor_until?: string | null
  /** The statute's floor alone; `null` for a type no statute names. */
  statutory_floor_until?: string | null
  org_rule?: { document_type?: string; owner_app?: string | null; months?: number; until?: string } | null
  override_until?: string | null
  /**
   * W9 (FR-25 as amended, D46): the sibling app whose document this is and
   * which keeps its life, when no rule the organisation chose matches it —
   * `retain_until` is then `null`. `null` whenever a date applies (an override,
   * a matching rule, any ProDoc document).
   */
  kept_by?: string | null
  settings_version?: number | null
  [key: string]: unknown
}

/**
 * The app that keeps a document's life (FR-25 as amended, D46), or `null`
 * when it has a date. Kept means **no `retain_until`**: it is never due, never
 * flagged and never expired by ProDoc, and its floor still holds an override.
 */
export function keptByOf(doc: Pick<ProDocDocument, 'retain_until' | 'retention_basis'>): string | null {
  const app = doc.retention_basis?.kept_by
  return !doc.retain_until && typeof app === 'string' && app ? app : null
}

/**
 * `meta.facets` of the register's list: for each dimension, value → how many
 * documents it would give with **every other** filter as it is, under the
 * caller's sight (plan §5.2). W3 added the document type, W4 the review state,
 * W5 the tag and the uploader.
 */
export interface DocumentFacets {
  workspace: Record<string, number>
  owner_app: Record<string, number>
  status: Record<string, number>
  document_type?: Record<string, number>
  /** W4: how many documents sit in each review state, with the other filters applied. */
  review_state?: Record<string, number>
  /** W5: tag id → documents carrying it. */
  tag?: Record<string, number>
  /** W5: user id → documents whose first version they uploaded. */
  uploaded_by?: Record<string, number>
}

/** The register's sort keys (plan §5.2): W4 added the review queue's, W5 the rest. */
export type DocumentSort = 'uploaded_at' | 'title' | 'document_date' | 'document_type' | 'size' | 'review_requested_at'

export const DOCUMENT_SORTS: DocumentSort[] = ['uploaded_at', 'title', 'document_date', 'document_type', 'size', 'review_requested_at']

/**
 * The register's question (FR-15, D18). One value per filter except `tag`,
 * which is *any of* and travels as a repeated parameter. Dates are
 * `YYYY-MM-DD`, inclusive at both ends.
 */
export interface DocumentQuery {
  /** Searches the title, file name, type, `source_ref`, notes, custom-field values (D18's `search_text`) and tag names. */
  q?: string
  /** A workspace id. (W1's `none` is retired — every document is in a workspace, D9.) */
  workspace?: string
  /** W5: a folder id — its own documents, or with `include_subfolders` every folder below it too. */
  folder?: string
  include_subfolders?: boolean
  /** W5: tag ids — a document carrying **any** of them. */
  tag?: string[]
  owner_app?: string
  /** W5: a user id — whose upload it was. */
  uploaded_by?: string
  document_type?: string
  /** W5: the date on the document. */
  document_date_from?: string
  document_date_to?: string
  /** W5: the day it was uploaded. */
  uploaded_from?: string
  uploaded_to?: string
  /** Archived and expired rows answer only a holder of `can_delete` on `document` (FR-24, FR-26). */
  status?: DocumentStatus
  /** W6: `flagged` — live documents past their `retain_until`, kept until someone decides (FR-26). */
  retention?: 'flagged'
  /** W4: one review state — `needs_review` is the review queue. */
  review_state?: ReviewState
  /**
   * W4: leave out the documents whose current version the caller uploaded —
   * the four-eyes walk (FR-23). Sent only when true.
   */
  exclude_own?: boolean
  /** W6: `shared` — what members' shares put in the caller's sight: documents, and everything under a shared folder (FR-29). */
  view?: 'shared'
  /** W22 (D52): the current version's reading in this state — the home screen's tiles open the register with it. */
  reading?: ReadingState
  /** W22 (FR-58): `locked` — encrypted documents whose current version has no completed reading yet. */
  password?: 'locked'
  /** W23 (FR-51, C-W23): the current version holds this kind — or `none` was found, it was `not_scanned`, or it is still `pending`. */
  personal_data?: PersonalDataFilter
  sort?: DocumentSort
  dir?: 'asc' | 'desc'
  /** Exact match — a sibling's deep link into its own record's documents. */
  source_ref?: string
  limit?: number
  offset?: number
}

/** A list's `meta`, as the package reads it. */
export interface ListMeta {
  total: number
  limit: number
  offset: number
  /** The register's lists only. */
  facets?: DocumentFacets
  /**
   * The names the service could put to the user ids in this answer (the
   * uploaded-by facet), from its identity projection — eventually consistent,
   * so an id without a name is a member not yet projected, never a guess.
   */
  people?: Record<string, string | null>
  /**
   * W22 (FR-23 refined, D59): the organisation's effective four-eyes switch,
   * on the register's list — so the review queue knows whether to walk the
   * caller's own uploads without a settings read. Absent from an older service.
   */
  four_eyes?: boolean
}

export interface Page<T> extends ListMeta {
  items: T[]
}

/** One entry of the organisation's document-type list (D10) — data in settings, never a constant. */
export interface DocumentTypeOption {
  code: string
  label: string
  enabled?: boolean
}

/**
 * `GET /api/v1/documents/options` — the vocabularies a screen must never
 * hard-code: the `owner_app` slugs the closed list accepts, the media types a
 * file may be (decided by signature on the service), the per-file ceiling, the
 * per-request file cap (D8), the statuses, the **enabled document types** (FR-5)
 * and the service's own slug.
 */
export interface DocumentOptions {
  owner_apps: string[]
  media_types: string[]
  max_upload_bytes: number
  /** D8: `PRODOC_MAX_FILES_PER_REQUEST`, default 20. */
  max_files_per_request?: number
  statuses: DocumentStatus[]
  /** The enabled types, in the organisation's order. */
  document_types?: DocumentTypeOption[]
  /** W4: the review states, for the register's filter. */
  review_states?: ReviewState[]
  app: string
}

/** The service's default when `/options` does not say (D8). */
export const DEFAULT_MAX_FILES_PER_REQUEST = 20

/**
 * W9 (FR-39, D45): where a sibling app files a document, named by the app — a
 * `/`-joined path (`Clients/ACME Traders/FY 2025-26`) or its names as a list.
 * Under the folder given as `folder_id` when there is one, else under the
 * workspace's top level; the service makes every missing folder once, kept by
 * the app.
 */
export type FolderPath = string | readonly string[]

/** Control characters — never part of a folder's name (FR-38). */
const CONTROL = /[\u0000-\u001f\u007f]/

/**
 * A path's names as the service will walk them: each trimmed, the empty ones
 * dropped — `' Clients / ACME '` and `['Clients', 'ACME']` are both
 * `['Clients', 'ACME']`. A string splits on `/`; a list's names are taken
 * whole, so a `/` inside one stays inside it for `folderPathError` to name.
 */
export function folderPathNames(path: FolderPath | null | undefined): string[] {
  if (path === null || path === undefined) return []
  const names = typeof path === 'string' ? path.split('/') : [...path]
  return names.map((name) => (typeof name === 'string' ? name.trim() : '')).filter(Boolean)
}

/**
 * What is wrong with a path before it is sent, or `undefined` (FR-38's rules,
 * which the service applies again): a name with `/`, `\` or a control
 * character, a name over 120 characters, or more names than folders nest. The
 * depth below a `folder_id` is the service's to judge (`too_deep`).
 */
export function folderPathError(path: FolderPath | null | undefined): string | undefined {
  const names = folderPathNames(path)
  for (const name of names) {
    if (/[/\\]/.test(name)) return `A folder name cannot contain / or \\ — “${name}”.`
    if (CONTROL.test(name)) return 'A folder name cannot contain a control character.'
    if (name.length > MAX_FOLDER_NAME) return `A folder name is up to ${MAX_FOLDER_NAME} characters.`
  }
  if (names.length > MAX_FOLDER_DEPTH) return `Folders nest at most ${MAX_FOLDER_DEPTH} levels; this path has ${names.length}.`
  return undefined
}

/** What to do with bytes the organisation already holds (D7). *Discard* is its own call. */
export type OnDuplicate = 'keep_both' | 'new_version'

/** One file of an upload: a `File`, or bytes with the name to store them under. */
export type UploadFile = File | { file: Blob; file_name: string }

/**
 * `POST /api/v1/documents` — one request, many files (D8). What applies to
 * the batch applies to every file in it; `title` therefore makes sense only
 * for a batch of one (the title defaults to each file's name).
 */
export interface UploadInput {
  files: UploadFile[]
  /** Omitted: the organisation's default workspace (D9). */
  workspace_id?: string | null
  /** A code from the enabled list; omitted, `other`. */
  document_type?: string
  title?: string
  /** `YYYY-MM-DD`. */
  document_date?: string | null
  /** Defaults to `prodoc` on the service. A sibling slug is accepted only while its pair is active (FR-32). */
  owner_app?: string
  source_ref?: string | null
  /** The uploader's answer to a duplicate (D7): store a second record, or add the bytes as a new version of `duplicate_of`. */
  on_duplicate?: OnDuplicate
  duplicate_of?: string
  /** W5: a folder of the target workspace to file every file of the batch in; omitted, the workspace's root (FR-38). */
  folder_id?: string | null
  /**
   * W9 (FR-39, D45): the folder path a **sibling's** `owner_app` names —
   * relative to `folder_id` when both are given. The service refuses it with
   * ProDoc's own `owner_app`, whose folders a person chooses. Sent as one
   * `/`-joined `folder_path`, after `folderPathError` has found nothing wrong.
   */
  folder_path?: FolderPath | null
  /**
   * Engine W15 (T15.15, FR-E40): read each file as it is stored — the `parse`
   * form field, a JSON object, committed with the version or not at all. A
   * refused reading stores nothing; each stored file's answer carries its job.
   * Omitted, nothing is parsed and no field is sent.
   */
  parse?: ParseOnUpload | null
  /**
   * W30 (FR-67, D100): the password of an encrypted PDF, to be checked by the
   * service and kept sealed with the version — **one file per request**, as the
   * form carries one field. A wrong one still stores the file, *Locked*, and the
   * answer says `password_error`. Never logged, never kept by the browser.
   */
  password?: string | null
}

/** W30 (FR-67, D100): why a password sent with an upload was not kept — the service's `password_error`. */
export type PasswordError = 'password_incorrect' | 'not_encrypted' | 'password_unsupported' | (string & {})

/**
 * One file's answer (D8). `index` is its place in the request. A refusal
 * carries the service's code and `details.reason` — `CONFLICT` with
 * `reason: duplicate` names the twins in `details.duplicate_of`, and nothing
 * was stored. A stored file's `job` is the parse job its `parse` field made
 * (T15.15) — `null` when none was asked.
 */
export type UploadOutcome =
  | {
      ok: true
      index: number
      file_name: string
      document: ProDocDocument
      duplicate_of: string[]
      job: ParseJob | null
      /** W30 (FR-67): whether the password sent with it was kept — absent when none was sent, or from an older service. */
      password_saved?: boolean
      /** W30: why it was not kept; the file is stored all the same. */
      password_error?: PasswordError | null
    }
  | { ok: false; index: number; file_name: string; error: ApiError }

export interface UploadResult {
  results: UploadOutcome[]
  stored: number
  refused: number
}

/** `PATCH /api/v1/documents/{id}` (plan §5.2). The file name is a version's fact and is not here. */
export interface DocumentPatch {
  title?: string
  document_type?: string
  document_date?: string | null
  notes?: string | null
  custom_fields?: Record<string, CustomValue>
  /** Another workspace of this organisation; the document lands in its root. */
  workspace_id?: string
  /** W5: a folder of its (new) workspace, or `null` for the workspace's root (FR-38). */
  folder_id?: string | null
}

/** Why the bytes are read — the service writes it into the read log before it hands out a URL (D4). */
export type ReadPurpose = 'view' | 'download'

/**
 * What a `/file` read hands back: a URL the browser may open or embed for the
 * presign's five minutes, and the bytes on demand, with the name from the
 * answer or `Content-Disposition` — the key carries none (FR-10).
 */
export interface FileHandle {
  url: string | null
  fileName: string | null
  mediaType: string | null
  /** Seconds the URL lives, when the service said. */
  expiresIn: number | null
  blob: () => Promise<Blob>
}

/* --------------------------------------------------------------- versions */

/** Why a version exists (plan §4.2); `imported` — W9 (FR-42): history an app brought, stored at its original time. */
export type VersionReason = 'initial' | 'replacement' | 'resubmission' | 'duplicate_linked' | 'app_supersede' | 'imported'

/** One immutable version of a document's bytes (FR-9). Its storage key is never sent. */
export interface DocumentVersion {
  id: string
  document_id: string
  version_no: number
  file_name: string
  media_type: string
  /** What the client said the file was, when it differed from what the bytes are (FR-2). */
  declared_media_type: string | null
  size_bytes: number
  sha256: string
  page_count: number | null
  encrypted: boolean
  supersedes_id: string | null
  reason: VersionReason | string
  /** The member who stored it — a person here, or the member an app acted for (FR-40). */
  uploaded_by: string | null
  /** W9 (FR-40): the someone outside the organisation an app stored it for, `<kind>:<uuid>`; never a name. */
  uploaded_party?: string | null
  actor_kind: string
  actor_service: string | null
  /** When it was stored — for an `imported` version, its original upload time (D40). */
  created_at: string
  /** Exactly one version is current. */
  is_current: boolean
  /** W30 (FR-67, D98): its password is saved, sealed — never the value. Saved once, then final. */
  password_saved?: boolean
}

export interface VersionInput {
  file: UploadFile
  /** The version's reason code. A person's new version is a `replacement` — the only value the service takes on this route. */
  reason?: 'replacement'
  /**
   * W22 (FR-55, D53): read the new version as this — the `parse` form field
   * exactly as the upload takes it, committed with the version or not at all,
   * so *Upload again* reads the new file as the old one was asked. Omitted,
   * nothing is parsed and no field is sent.
   */
  parse?: ParseOnUpload | null
  /** W30 (FR-67, D100): the new version's password, checked and kept sealed as an upload's is. */
  password?: string | null
}

/**
 * What `versions.add` answers: the document as it now stands, and — when a
 * `parse` was asked — the job made with the new version (C-W22, D53). `job`
 * is absent when nothing was asked or the service named none.
 */
export type VersionAdded = ProDocDocument & { job?: ParseJob }

/* ------------------------------------------------------------------ audit */

/** Who did it: a person, a sibling app, a share link's reader, or the service itself. */
export type ActorKind = 'user' | 'app' | 'link' | 'system'

export interface AuditActor {
  kind: ActorKind | string
  /** A person — or, on an `app` row, the member the app acted for (FR-40). */
  user_id: string | null
  /** A sibling app's credential name (`prodesk-service`), for an `app` actor. */
  service: string | null
  /** From the identity projection — eventually consistent, so `null` when it has no name yet, never a guess. */
  display_name: string | null
  /** W9 (FR-40), an `app` row only: the someone outside the organisation it acted for, `<kind>:<uuid>`. */
  party?: string | null
}

/**
 * One line of a document's history (`GET /documents/{id}/audit`): an
 * **audit** row — a mutation, its changed fields as `{field: {old, new}}`
 * (D5) — or a **read** (`action: document.read`) — one presigned URL handed
 * out, with its purpose (D4). Newest first, keyset-paged. W9: whom an app
 * acted for rides `actor` (`user_id`, `party`), whether the service nests it
 * or sends `actor_kind` / `actor_user_id` / `actor_service` / `actor_party`
 * flat (`auditEntryOf`); an imported version's row has `reason: imported` and
 * `changes.uploaded_at`, and an imported read its original `occurred_at`.
 */
export interface AuditEntry {
  id: string
  kind: 'audit' | 'read'
  occurred_at: string
  /** `document.uploaded`, `document.metadata_changed`, `document.version_added`, `document.archived`, … `document.read`. */
  action?: string | null
  /** Reads: why the bytes were fetched — `view`, `download`, … */
  purpose?: string | null
  actor: AuditActor
  /** Mostly `{field: {old, new}}`; a duplicate choice records `{choice, duplicate_of, sha256}`. */
  changes?: Record<string, unknown>
  /** The audit row's reason — for a version, why it exists (`replacement`, `duplicate_linked`, `imported`, …). */
  reason?: string | null
  version_id?: string | null
}

export interface AuditPage {
  items: AuditEntry[]
  /** Pass as `before` for the next, older page; null at the beginning of the history. */
  next: string | null
}

/* --------------------------------------------------------------- settings */

/** One document type as settings hold it (D10): the thirteen shipped codes can be disabled, never removed; `other` is always on. */
export interface DocumentTypeSetting {
  code: string
  label: string
  enabled: boolean
}

/**
 * The review section (plan §4.3, W4): whether a new upload of each type enters
 * `needs_review` (FR-20 — on for `invoice`, `bank_statement`, `credit_note`,
 * `debit_note` as shipped), and the four-eyes switch (FR-23 — **off** by
 * default since W22, D59; an organisation that saved it keeps its value).
 */
export interface ReviewSettings {
  defaults: Record<string, boolean>
  four_eyes: boolean
}

/**
 * The shipped default of the four-eyes switch — **off** since W22 (FR-23
 * refined, D59: a person who may approve approves their own upload unless the
 * organisation turns it on). The service's `FOUR_EYES_BY_DEFAULT`; an
 * organisation whose stored settings say `true` keeps refusing.
 */
export const FOUR_EYES_DEFAULT = false

/**
 * One retention rule of the organisation's (plan §4.3, W6): keep documents of
 * this type — for one owning app, or (`null`) whichever app brought them — for
 * this many months. Months count from the end of the financial year that holds
 * the anchor, or after upload for the org-minimum types (`contract`, `other`).
 * A rule below its type's statutory floor is refused `below_floor` (D22).
 */
export interface RetentionRule {
  document_type: string
  owner_app: string | null
  months: number
}

export interface RetentionSettings {
  rules: RetentionRule[]
}

/** The link-share defaults (FR-29, FR-36): how long a new link works (1–30 days), and whether it must carry a passcode. */
export interface SharingSettings {
  link_expiry_days: number
  link_passcode_required: boolean
}

/** The shipped sharing defaults (plan §4.3) — what a screen assumes before the settings arrive. */
export const SHARING_DEFAULTS: SharingSettings = { link_expiry_days: 7, link_passcode_required: false }
export const MIN_LINK_DAYS = 1
export const MAX_LINK_DAYS = 30

/**
 * One statutory floor (D22, `app/domain/retention_floors.py`): a rule of law,
 * the months it keeps a record after the end of the financial year, its
 * citation, and the document types it applies to. Data shipped with the
 * service, never edited here — an org rule can lengthen, never shorten it.
 */
export interface RetentionFloor {
  /** The rule's key — `cgst_36`, `it_rule_6f`, `companies_128_5`, `org_minimum`, … */
  rule: string
  /** What a reader calls it. */
  label: string
  citation: string
  months: number
  /** `fy_end` — after the end of the financial year holding the anchor; `upload` — after the upload. */
  anchor: string | null
  document_types: string[]
  /** Whether the citation was re-verified at build time (T6.2); `null` when the service does not say. */
  verified: boolean | null
}

/** A document type's effective floor: the longest of the floors it carries (D22), and every one of them. */
export interface TypeFloor {
  document_type: string
  months: number
  floors: RetentionFloor[]
}

/** The settings document (plan §4.3); W3 carries the first section, later waves add theirs. */
export interface OrgSettingsDocument {
  document_types: DocumentTypeSetting[]
  /** W4. */
  review?: ReviewSettings
  /** W6. */
  retention?: RetentionSettings
  /** W6. */
  sharing?: SharingSettings
  [section: string]: unknown
}

/**
 * `GET /api/v1/settings`: what is in force, what the organisation changed, the
 * shipped defaults, the version to send back — and from W6 the floor table, so
 * the page can say "the statute's minimum" beside every rule (plan §4.3).
 */
export interface SettingsAnswer {
  effective: OrgSettingsDocument
  overrides: Partial<OrgSettingsDocument>
  defaults: OrgSettingsDocument
  version: number
  floors?: RetentionFloor[]
}

/** `PATCH /api/v1/settings` — the sections changed, with the `version` read; a stale version is `CONFLICT` (`version_conflict`). */
export interface SettingsPatch {
  version: number
  document_types?: DocumentTypeSetting[]
  review?: ReviewSettings
  retention?: RetentionSettings
  sharing?: SharingSettings
  [section: string]: unknown
}

/** The sharing section in force, the shipped default where the service has none. */
export function sharingOf(settings: SettingsAnswer | null | undefined): SharingSettings {
  const s = settings?.effective?.sharing
  return {
    link_expiry_days: typeof s?.link_expiry_days === 'number' ? s.link_expiry_days : SHARING_DEFAULTS.link_expiry_days,
    link_passcode_required: typeof s?.link_passcode_required === 'boolean' ? s.link_passcode_required : SHARING_DEFAULTS.link_passcode_required,
  }
}

/** Each document type's effective floor, from the table: the longest floor that names the type (D22). */
export function floorsByType(floors: RetentionFloor[]): Map<string, TypeFloor> {
  const out = new Map<string, TypeFloor>()
  for (const floor of floors) {
    for (const type of floor.document_types) {
      const current = out.get(type) ?? { document_type: type, months: 0, floors: [] }
      current.floors.push(floor)
      current.months = Math.max(current.months, floor.months)
      out.set(type, current)
    }
  }
  return out
}

/** Whether four-eyes is on, from a settings answer — the shipped default when the section is absent. */
export function fourEyesOf(settings: SettingsAnswer | null | undefined): boolean {
  const value = settings?.effective?.review?.four_eyes
  return typeof value === 'boolean' ? value : FOUR_EYES_DEFAULT
}

/* ----------------------------------------------------------------- review */

/** An approve or a reject (plan §5.2): the comment is optional and at most 1,000 characters. */
export interface ReviewDecisionInput {
  comment?: string | null
  /**
   * The version the reviewer looked at. If a newer one became current since,
   * the service refuses `CONFLICT` (`version_conflict`) rather than landing the
   * decision on bytes nobody judged. Omitted, the current version is judged.
   */
  version_id?: string | null
}

/** The service's ceiling on a review comment. */
export const MAX_REVIEW_COMMENT = 1000

/* -------------------------------------------------------------- workspaces */

export type WorkspaceStatus = 'active' | 'archived'

export interface Workspace {
  id: string
  org_id: string
  name: string
  description: string | null
  status: WorkspaceStatus
  /** The organisation's default workspace (D9): made at first use, seen by every member, never archived. */
  is_default?: boolean
  created_by: string | null
  created_at: string
  updated_at: string
  /** Stored documents filed in it, where the service counts them; the archive refusal carries the number regardless. */
  document_count?: number
  member_count?: number
  /**
   * W22 (FR-61, ADR-0018, D58): the sibling app whose own workspace this is —
   * made the first time the app files without naming one — or `null` for a
   * workspace people made. The Workspaces screen says *iTax's workspace*.
   */
  app?: { slug: string; label: string } | null
}

export interface WorkspaceInput {
  name: string
  description?: string | null
}

/** Visibility narrowing, never a permission (plan §4.2): who sees this workspace's documents. */
export type MemberKind = 'member' | 'granted'

export interface WorkspaceMember {
  user_id: string
  kind: MemberKind
  /** From the user projection, where the service joins it. */
  name?: string | null
  email?: string | null
  created_at?: string
}

/* ----------------------------------------------------------------- folders */

export type FolderStatus = 'active' | 'archived'

/** How deep folders nest below a workspace's root (§9-16): the tenth level is refused `too_deep`. */
export const MAX_FOLDER_DEPTH = 9

/**
 * A folder (FR-38, D16) — **a record only**: keys stay id-only (FR-10), so a
 * rename or a move never touches the bucket, and moving a folder rewrites no
 * document row. It belongs to exactly one workspace and moves only within it.
 */
export interface Folder {
  id: string
  org_id?: string
  workspace_id: string
  /** `null` — at the workspace's root. */
  parent_id: string | null
  name: string
  /** 1 directly under the workspace's root, at most 9. */
  depth: number
  /** Its place as ids, the workspace's root first and the folder itself last. */
  path?: string[]
  /**
   * `prodoc` for a folder a person made here; a sibling app's slug for one it
   * keeps (§9-19) — whose structure is that app's (`APP_OWNED`) while people
   * still file into it.
   */
  owner_app: string
  status: FolderStatus
  created_by?: string | null
  created_at?: string
  updated_at?: string
  archived_at?: string | null
  /** Live documents filed directly in it, where the service counts them. */
  document_count?: number
  /** Active folders directly under it, where the service counts them — `0` is a leaf. */
  child_count?: number
}

/** One step of a breadcrumb, from the workspace's root down. */
export interface FolderCrumb {
  id: string
  name: string
}

/** `GET /folders/{id}`: the folder, its breadcrumb (ancestors, then itself) and its counts. */
export interface FolderDetail extends Folder {
  breadcrumb: FolderCrumb[]
}

export interface FolderInput {
  workspace_id: string
  /** Omitted or `null`: directly under the workspace's root. */
  parent_id?: string | null
  name: string
}

/** `PATCH /folders/{id}`: a new name, a new parent of the same workspace (`null` = the root), or both. */
export interface FolderPatch {
  name?: string
  parent_id?: string | null
}

/**
 * `POST /folders/paths` (FR-7): a dropped folder's structure, made once and
 * idempotently — every path a list of names from `parent_id` down. The answer
 * maps each path, joined by `/`, to its folder's id.
 */
export interface EnsurePathsInput {
  workspace_id: string
  parent_id?: string | null
  paths: string[][]
}

/** A folder name's ceiling (plan §4.2: `varchar(120)`). */
export const MAX_FOLDER_NAME = 120

/* -------------------------------------------------------------------- tags */

/** A tag's colour is one of the kit's tones (D17) — a `Badge` paints it; no hex ever travels. */
export type TagColor = 'neutral' | 'info' | 'success' | 'warning' | 'error'

export const TAG_COLORS: TagColor[] = ['neutral', 'info', 'success', 'warning', 'error']

/** A tag's name ceiling (plan §4.2: `varchar(60)`). */
export const MAX_TAG_NAME = 60

/**
 * An organisation's label (FR-14, D17): unique by name whatever its case. A
 * document carries it by id, so a rename or a recolour is one row and touches
 * no document; tag text never reaches an event (FR-34).
 */
export interface Tag {
  id: string
  name: string
  color: TagColor | string
  /** Documents carrying it, within the caller's sight. */
  document_count?: number
  created_by?: string | null
  created_at?: string
  updated_at?: string
}

export interface TagInput {
  name: string
  color?: TagColor
}

/**
 * `PUT /documents/{id}/tags` — the whole set the document carries from now on:
 * existing tags by id, and new names made on the way (which needs `can_create`
 * on `tag` beside the edit, D17).
 */
export interface DocumentTagsInput {
  tag_ids: string[]
  new_tags?: TagInput[]
}

/* -------------------------------------------------------------------- bulk */

/** The bulk verbs (FR-16): each document is gated on its own, and answered on its own. */
export type BulkAction = 'tag' | 'untag' | 'move_folder' | 'move_workspace' | 'archive'

/** A selection's ceiling, for bulk and for the ZIP (plan §5.2). */
export const MAX_SELECTION = 200

export interface BulkInput {
  action: BulkAction
  document_ids: string[]
  /** `tag` / `untag`. */
  tag_ids?: string[]
  /** `move_folder`: a folder of the documents' workspace, or `null` for its root. */
  folder_id?: string | null
  /** `move_workspace`: the documents land in its root. */
  workspace_id?: string
}

/** One document's answer: changed (with the record as it now stands, when the service sends it) or refused with its reason. */
export type BulkOutcome =
  | { ok: true; document_id: string; document?: ProDocDocument }
  | { ok: false; document_id: string; error: ApiError }

export interface BulkResult {
  results: BulkOutcome[]
  changed: number
  refused: number
}

/* --------------------------------------------------------------------- ZIP */

/**
 * `POST /documents/zip` (D20): a single-use ticket — the requester's own, for
 * 60 seconds. The browser **navigates** to `url`, so its download manager
 * writes the streamed archive to disk; a `fetch()` to a blob would hold the
 * whole archive in the tab. The URL carries the ticket, never a file name.
 */
export interface ZipTicket {
  download_id: string
  /** Absolute, against the service's origin. */
  url: string
  expires_in: number
}

/** The archive's ceiling (D20): 2 GiB — beyond it `VALIDATION_ERROR` (`zip_too_large`). */
export const MAX_ZIP_BYTES = 2 * 1024 * 1024 * 1024

/* ----------------------------------------------------------------- related */

/** Why a document is related (FR-18): the same `source_ref`, the same bytes, or a person's explicit link. */
export type RelationKind = 'source_ref' | 'sha256' | 'link'

export interface RelatedDocument {
  document: ProDocDocument
  /** Every reason it is related, in the order above. */
  relations: RelationKind[]
}

/* ----------------------------------------------------------------- summary */

/** One line of the dashboard's recent activity — an audit row, named as the history names it. */
export interface SummaryActivity {
  id: string
  occurred_at: string
  action: string | null
  document_id: string | null
  /** The document's title, within the caller's sight. */
  document_title: string | null
  actor: AuditActor
}

/**
 * `GET /summary` (FR-31): the dashboard's every number, for the caller's
 * visible scope — except storage, which is the organisation's whole use against
 * app-management's `storage` limit (`null` when the plan sets none).
 */
export interface Summary {
  total: number
  awaiting_review: number
  /** Uploaded since the first of this month, India time. */
  uploaded_this_month: number
  by_workspace: Record<string, number>
  by_type: Record<string, number>
  recent_uploads: ProDocDocument[]
  recent_activity: SummaryActivity[]
  storage: { used_bytes: number; limit_bytes: number | null }
  /** Engine W15 (T15.5): the extractions waiting for a reviewer — the service's `extractions.needs_review`. */
  extractions_needs_review: number
  /**
   * W22 (FR-57, D54): the pipeline's five tiles. `uploaded` and `accepted` are
   * **in the period**; `reading`, `needs_review` and `failed` are **now**.
   */
  pipeline: SummaryPipeline
  /** W22 (FR-57, D54): the 14 IST days ending today, oldest first — whatever the period. */
  daily: SummaryDay[]
  /** W22: the IST days the period-bound numbers cover (`meta.period`); `null` from an older service. */
  period: { from: string; to: string } | null
}

/** The home screen's tiles (FR-57) — the library's filtered counts, each. */
export interface SummaryPipeline {
  uploaded: number
  reading: number
  needs_review: number
  accepted: number
  failed: number
}

/**
 * One IST day of the chart (FR-57, D54): documents first stored that day;
 * documents approved or whose extraction was accepted that day (once each);
 * and of that day's uploads, those still waiting for a review now.
 */
export interface SummaryDay {
  /** `YYYY-MM-DD`, India time. */
  date: string
  uploaded: number
  approved: number
  pending: number
}

/** `GET /summary`'s period: IST days, both optional — the service's default is this month. */
export interface SummaryPeriod {
  from?: string | null
  to?: string | null
}

/* ------------------------------------------------------------------ shares */

/**
 * A share (D26): to **members** of the organisation (sight, never a verb — the
 * reader still needs `document:can_view`), by **link** for someone outside the
 * platform, or to a sibling **app** (an administrator's, FR-32).
 */
export type ShareKind = 'member' | 'link' | 'app'

/** A link's audience: people of the organisation, signed in — or anyone holding it (behind step-up). */
export type ShareAccess = 'org' | 'anyone'

/** One thing a share reaches: a document, or a folder — live, with everything under it at read time (§9-18). */
export interface ShareItem {
  document_id: string | null
  folder_id: string | null
}

/** A share as the service lists it. The link's token is **never** here — only its hash is kept, and the token is shown once. */
export interface Share {
  id: string
  kind: ShareKind
  access: ShareAccess | null
  recipient_user_id: string | null
  recipient_app: string | null
  /** The items it reaches. */
  items: ShareItem[]
  expires_at: string | null
  revoked_at: string | null
  revoked_by: string | null
  created_by: string | null
  created_at: string | null
  last_opened_at: string | null
  /** Whether the link asks for a passcode; the passcode itself never comes back. */
  has_passcode: boolean
  /** Locked after ten wrong passcodes, until then (D26). */
  locked_until: string | null
  /** The last send through comm: as the org or the platform, by which channel, when (FR-30). */
  sent_as: string | null
  sent_channel: string | null
  sent_at: string | null
  /** For `GET /documents/{id}/shares`: how this one reaches the document — directly, or through a folder. */
  via_folder_id: string | null
}

/** Whether a share still opens: not revoked, and not past its expiry. */
export function shareIsLive(share: Pick<Share, 'revoked_at' | 'expires_at'>, now = new Date()): boolean {
  if (share.revoked_at) return false
  return !share.expires_at || new Date(share.expires_at).getTime() > now.getTime()
}

/** `POST /shares` (plan §5.2): what is shared, and with whom. Ceilings: 200 documents, 50 folders. */
export interface ShareInput {
  kind: ShareKind
  document_ids?: string[]
  folder_ids?: string[]
  /** `member`. */
  recipient_user_ids?: string[]
  /** `link`: `anyone` needs step-up. */
  access?: ShareAccess
  /** `link`: 1–30; omitted, the organisation's default (settings → sharing). */
  expires_in_days?: number
  /** `link`: optional unless the organisation requires one. Never kept by the client. */
  passcode?: string
  /** `app`: the sibling's slug (an administrator's, `settings:can_update`). */
  recipient_app?: string
}

export const MAX_SHARE_DOCUMENTS = 200
export const MAX_SHARE_FOLDERS = 50

/**
 * What `POST /shares` answers: the share(s) made — one per member for a
 * member share — and, **for a link, the token once**. The token is shown, copied
 * and dropped; nothing in the client keeps it (T6.27's verify).
 */
export interface CreatedShare {
  shares: Share[]
  /** The link's token, this once. `null` for a member or app share. */
  token: string | null
  /** The service's own address for the link (`PRODOC_PUBLIC_APP_URL/s/<token>`), when it sends one. */
  url: string | null
}

/** A comm send (D27): e-mail or WhatsApp, at most ten recipients, a message of at most 500 characters. */
export type ShareChannel = 'email' | 'whatsapp'
export const SHARE_CHANNELS: ShareChannel[] = ['email', 'whatsapp']
export const MAX_SEND_RECIPIENTS = 10
export const MAX_SEND_MESSAGE = 500

export interface ShareSendInput {
  channel: ShareChannel
  recipients: string[]
  message?: string
  /**
   * The link's token, while it is still on screen — the service keeps only its
   * hash, so a message carrying `/s/<token>` can only be composed by someone
   * who holds it (`app/api/shares.py`, `send_share`). It travels as
   * `X-Share-Token`, never in the body's words and never in a URL.
   */
  token: string
}

/** The service's passcode bounds for a link (`app/domain/sharing.py`: `PASSCODE_MIN`, `PASSCODE_MAX`). */
export const PASSCODE_MIN = 4
export const PASSCODE_MAX = 64
/** At most this many members in one member share (`app/api/shares.py`: `MAX_MEMBERS`). */
export const MAX_SHARE_MEMBERS = 50

/**
 * `{sent_as, channel, sent, failed}` — `org` when the organisation's own
 * connected channel carried it, `platform` otherwise; how many recipients it
 * reached. Nothing sent at all is the service's `CONFLICT`
 * (`provider_not_configured` or `not_sent`), relayed as it said it.
 */
export interface ShareSendResult {
  sent_as: string | null
  channel: string | null
  sent: number | null
  failed: number | null
}

/**
 * `GET /sharing/channels` (D27): each family as `integration-service` reports
 * it for the organisation — whether its own channel is connected, the
 * provider, what a send would leave as, and why not when it would not.
 */
export interface ChannelState {
  channel: ShareChannel | string
  active: boolean
  provider: string | null
  /** `org`, `platform`, or `null` when nothing can send. */
  sends_as: string | null
  reason: string | null
}

/* ----------------------------------------------------------------- erasure */

/** What happened to one document of an erasure request (D25). */
export type ErasureOutcome = 'erased' | 'held_by_floor' | 'already_expired'

export interface ErasureItem {
  document_id: string
  outcome: ErasureOutcome | string
  /** For a held document: the day the statute lets it go, and the floors that hold it. */
  floor_until: string | null
  floor_rules: { rule: string; citation: string; until?: string | null }[]
  erased_at: string | null
}

/** A DPDP erasure request (FR-28, D25): the organisation's own reference, the documents named, and what each came to. */
export interface ErasureRequest {
  id: string
  reference: string
  reason: string | null
  requested_by: string | null
  document_count: number
  erased_count: number
  held_count: number
  completed_at: string | null
  created_at: string | null
  /** On the single read, and on the create's answer. */
  items: ErasureItem[]
}

export interface ErasureInput {
  /** The organisation's own reference for the principal's request — opaque to ProDoc, at most 100 characters. */
  reference: string
  reason?: string
  document_ids: string[]
}

export const MAX_ERASURE_REFERENCE = 100
export const MAX_RETENTION_REASON = 500

/* ------------------------------------------------- a share link's reader */

/** One document a link reaches, as its reader sees it (plan §5.3): never its id's history, its notes or its custom fields. */
export interface SharedDocument {
  id: string
  title: string
  file_name: string
  size_bytes: number
  media_type: string | null
  document_type: string | null
}

/** One folder a link reaches — its name, and how much is directly in it where the service counts. */
export interface SharedFolder {
  id: string
  name: string
  document_count: number | null
  folder_count: number | null
}

/** `GET /public/share` — the bundle: when it stops working, who shared it, and what it reaches. */
export interface SharedBundle {
  expires_at: string | null
  access: ShareAccess | null
  org_name: string | null
  shared_by: string | null
  documents: SharedDocument[]
  folders: SharedFolder[]
}

/** `GET /public/share/folders/{id}` — a read-only listing inside the shared subtree. */
export interface SharedFolderListing {
  folder: SharedFolder
  /** From the shared root down to this folder. */
  breadcrumb: FolderCrumb[]
  folders: SharedFolder[]
  documents: SharedDocument[]
}

/* ----------------------------------------------------------------- client */

export interface ProDocClientOptions {
  /** The service's origin, e.g. `http://localhost:8170`. Routes are `/api/v1/*` under it. */
  baseUrl: string
  /** The host's authenticated fetcher — `session.authed` from `@fiveox/shell`. */
  fetcher: Fetcher
  /** The selected org, or a getter read on every call so a switch is honoured. */
  orgId: string | null | (() => string | null)
}

export interface ProDocClient {
  readonly baseUrl: string
  /** The org every call carries, as of now. */
  orgId: () => string | null
  access: () => Promise<AccessAnswer>
  documents: {
    options: () => Promise<DocumentOptions>
    list: (query?: DocumentQuery) => Promise<Page<ProDocDocument>>
    get: (id: string) => Promise<ProDocDocument>
    /** Many files, answered per file (D8). A refusal of the whole request — the file cap, an unknown workspace, a lapse — throws. */
    upload: (input: UploadInput) => Promise<UploadResult>
    /** Live documents with these bytes, within the caller's sight (D7). */
    duplicates: (sha256: string) => Promise<ProDocDocument[]>
    /** The uploader chose *discard*: nothing is stored, the choice is audited (FR-3). */
    discardDuplicate: (input: { document_id: string; sha256: string }) => Promise<unknown>
    update: (id: string, patch: DocumentPatch) => Promise<ProDocDocument>
    archive: (id: string) => Promise<ProDocDocument>
    /**
     * W37 (ADR-0025 §7): undo a split — its parts **archived**, the original's mark
     * cleared; nothing deleted (`document:can_delete`). Answers the original, and the
     * parts it archived. Not split: `CONFLICT` (`not_split`).
     */
    undoSplit: (id: string) => Promise<{ document: ProDocDocument; archived: string[] }>
    /** Back from the archive — `can_delete` **and** `can_view_all` on `document` (D11). */
    restore: (id: string) => Promise<ProDocDocument>
    file: (id: string, purpose?: ReadPurpose) => Promise<FileHandle>
    versions: {
      list: (id: string) => Promise<DocumentVersion[]>
      /** A new version (FR-9) — with W22's optional `parse`, read as asked in the same transaction, its job on the answer (D53). */
      add: (id: string, input: VersionInput) => Promise<VersionAdded>
      file: (id: string, versionId: string, purpose?: ReadPurpose) => Promise<FileHandle>
      /**
       * Engine W16 (C-E12, D44): page `page` (1-based) of the version as the
       * engine's sandbox draws it — a PNG of the page **as it lies**, at most
       * 150 DPI and 4,000 px a side — for a file the browser cannot draw (a
       * TIFF). A logged read (purpose `view`), `document:can_view`; `NOT_FOUND`
       * past the last page and for a document out of sight.
       */
      pagePreview: (id: string, versionId: string, page: number) => Promise<Blob>
    }
    /**
     * W30 (FR-67, D100): `PUT …/versions/{versionId}/password` — check the
     * password against the version's file and keep it **sealed**, used for the
     * reader and never shown (`document:can_update`). Saved once, then final:
     * there is no change and no forget. Refused `VALIDATION_ERROR`
     * (`password_incorrect`, `password_unsupported`) or `CONFLICT`
     * (`not_encrypted`, `password_already_saved`); nothing is kept then.
     */
    savePassword: (id: string, versionId: string, password: string) => Promise<void>
    audit: (id: string, page?: { before?: string | null; limit?: number }) => Promise<AuditPage>
    /**
     * The four-eyes state's transitions (D14), each answering the document as
     * it now stands. `request` (own+create, D12) moves `uploaded` →
     * `needs_review`; `approve` and `reject` (`can_update` on `document`)
     * decide the **current version** — refused `FOUR_EYES` when the caller
     * uploaded it and the switch is on, and `CONFLICT` (`illegal_transition`)
     * when the document is not waiting for review.
     */
    review: {
      request: (id: string) => Promise<ProDocDocument>
      approve: (id: string, input?: ReviewDecisionInput) => Promise<ProDocDocument>
      reject: (id: string, input?: ReviewDecisionInput) => Promise<ProDocDocument>
    }
    /** W5: the whole tag set it carries from now on (own+create; a new name also `can_create` on `tag`). */
    setTags: (id: string, input: DocumentTagsInput) => Promise<ProDocDocument>
    /** W5: the same `source_ref`, the same bytes, and explicit links — all within sight (FR-18). */
    related: (id: string) => Promise<RelatedDocument[]>
    links: {
      /** One row per pair, audited at both ends; a document outside sight is `NOT_FOUND`. */
      add: (id: string, relatedId: string) => Promise<unknown>
      remove: (id: string, relatedId: string) => Promise<unknown>
    }
    /** W5: one action over a selection (≤ 200), answered per document (FR-16). */
    bulk: (input: BulkInput) => Promise<BulkResult>
    /** W5: a single-use, 60-second ticket for the streamed ZIP (D20). */
    zip: (documentIds: string[]) => Promise<ZipTicket>
    /**
     * W6 (D24): an administrator's override of `retain_until`, with a reason —
     * `settings:can_update` **and** step-up; below the floor `below_floor`.
     */
    setRetention: (id: string, input: { retain_until: string | null; reason: string }, opts?: StepUpOptions) => Promise<ProDocDocument>
    /** W6: the shares that reach it, directly or through a folder (`share:can_view`). */
    shares: (id: string) => Promise<Share[]>
    /**
     * W23 (FR-51, FR-52, C-W23): what the current version holds of an
     * individual's personal data — **masked**, with where and how it was found.
     */
    personalData: {
      /** The current version's findings and its state; `meta.can_reveal` says whether this caller may reveal. Anyone who sees the document. */
      list: (id: string) => Promise<PersonalDataFindings>
      /**
       * One finding's value — `personal_data:can_view` and the document's
       * sight. The service logs the read **before** it answers, and answers
       * `no-store`; the request asks `no-store` too. `FORBIDDEN` without the
       * permission, `CONFLICT` `mismatch` when the file no longer holds it there
       * (`isRevealMismatch`). The caller keeps the value in a component's state only.
       */
      reveal: (id: string, findingId: string) => Promise<string | null>
    }
  }
  folders: {
    /** The folders directly under `parent_id` (omitted: under the workspace's root). */
    list: (query: { workspace_id: string; parent_id?: string | null }) => Promise<Folder[]>
    /** Every folder of the workspace the caller sees, flat, each with its `parent_id`. */
    tree: (workspaceId: string) => Promise<Folder[]>
    get: (id: string) => Promise<FolderDetail>
    create: (input: FolderInput) => Promise<Folder>
    /** A dropped folder's structure, made once (FR-7): path joined by `/` → folder id. */
    ensurePaths: (input: EnsurePathsInput) => Promise<Record<string, string>>
    /** Rename and/or move within its workspace — `APP_OWNED` for a sibling's folder. */
    update: (id: string, patch: FolderPatch) => Promise<Folder>
    /** Refused `CONFLICT` (`not_empty`) while it holds live documents or folders. */
    archive: (id: string) => Promise<Folder>
    /** W6: the folders members' shares put in the caller's sight — the roots of what was shared with them (FR-29). */
    shared: () => Promise<Folder[]>
  }
  /** W6: shares (D26) and sends through comm as the organisation (D27). */
  shares: {
    /** A link's token comes back **once**, in `token`; an `anyone` link needs step-up, an `app` share `settings:can_update`. */
    create: (input: ShareInput, opts?: StepUpOptions) => Promise<CreatedShare>
    list: (query?: { document_id?: string; folder_id?: string; mine?: boolean }) => Promise<Share[]>
    /** What others shared with the caller. */
    received: () => Promise<Share[]>
    /** The creator, or a holder of `share:can_delete`. A revoked link is `NOT_FOUND` at once. */
    revoke: (id: string) => Promise<Share>
    send: (id: string, input: ShareSendInput) => Promise<ShareSendResult>
    channels: () => Promise<ChannelState[]>
  }
  /** W6: DPDP erasure requests (FR-28, D25) — `settings:can_update`; recording one also needs step-up. */
  erasure: {
    create: (input: ErasureInput, opts?: StepUpOptions) => Promise<ErasureRequest>
    list: () => Promise<ErasureRequest[]>
    get: (id: string) => Promise<ErasureRequest>
  }
  tags: {
    list: () => Promise<Tag[]>
    create: (input: TagInput) => Promise<Tag>
    /** A rename or a recolour — no document is touched (D17). */
    update: (id: string, input: Partial<TagInput>) => Promise<Tag>
    /** Every document of `id` ends carrying `intoId` once; `id` is gone. */
    merge: (id: string, intoId: string) => Promise<unknown>
    /** Detached from every document, archived ones included (§9-17). */
    remove: (id: string) => Promise<unknown>
  }
  /** W5: the dashboard (FR-31); W22 (FR-57, D54): for a period of IST days — omitted, this month. */
  summary: (period?: SummaryPeriod) => Promise<Summary>
  /**
   * Engine W15 (FR-E3, FR-E9, FR-E13, FR-E19 – FR-E21): the parser, on request.
   * Every call is the person's — `extraction:can_create` to ask, `can_view` to
   * read, `can_update` to correct and decide — and a document out of sight is
   * `NOT_FOUND`, as everywhere.
   */
  parsing: {
    /** C-E3: the org's profiles, switched-off ones `enabled: false`; `offered`, `ai_available` and `ai_processing` from `meta`. */
    profiles: () => Promise<ProfileList>
    /**
     * FR-E3: ask for a parse. `202` with the queued job; `200` with the job it
     * repeats (`repeat`, FR-E5) or — inside `wait` — the ended job and its
     * extraction. Refused `FORBIDDEN` (`parsing_not_offered`) while the org's
     * screens do not offer parsing; `VALIDATION_ERROR` for a profile it does not
     * parse with (`details.allowed`); `PROFILE_MEDIA_MISMATCH`, `DOCUMENT_NOT_LIVE`
     * and `USAGE_LIMIT_EXCEEDED` before any work (FR-E8).
     */
    parse: (documentId: string, input: ParseInput) => Promise<ParseAnswer>
    /** FR-E9: where a job is; once completed, its extraction's id. */
    job: (jobId: string) => Promise<ParseJob>
    /**
     * W38 (FR-E59, D178): what each sheet of a workbook is — the profile that would read it,
     * its header row, its data rows and why. Unmetered, no extraction; the answer is erased an
     * hour after. `202` with the job to follow when it did not end inside `wait`; a file that
     * is no workbook or CSV is `PROFILE_MEDIA_MISMATCH`. Read a sheet then with
     * `parse(documentId, {profile, options: {sheet}})`.
     */
    survey: (documentId: string, input?: SurveyInput) => Promise<SurveyAnswer>
    /** FR-E13, FR-E18: a document's extractions, newest first, each with its review and `stale` — no fields. */
    extractions: (documentId: string) => Promise<ExtractionRecord[]>
    /**
     * One extraction with its fields — a read the service logs first (FR-E17); `EXTRACTION_NOT_READY` until the review allows (FR-E19).
     * W23 (FR-51): a person's read has each declared personal field masked, named in `masked_paths`.
     */
    extraction: (documentId: string, extractionId: string, options?: { rows?: 'summary' }) => Promise<Extraction>
    /**
     * W23 (FR-52, C-W23): one masked field's value — `personal_data:can_view`,
     * logged first, `no-store`, as a finding's reveal. The caller keeps it in a component's state only.
     */
    reveal: (documentId: string, extractionId: string, fieldPath: string) => Promise<RevealedField>
    /** FR-E21: the next revision of corrections and its recheck job — never an edit of the extraction. */
    correct: (documentId: string, extractionId: string, input: CorrectionsInput) => Promise<CorrectionAnswer>
    /** FR-E20: `needs_review → accepted`; anything else `CONFLICT`. */
    accept: (documentId: string, extractionId: string, input?: ExtractionDecisionInput) => Promise<ExtractionRecord>
    /** FR-E20: `needs_review → rejected` — the extraction ends; reading it again is a new request. */
    reject: (documentId: string, extractionId: string, input?: ExtractionDecisionInput) => Promise<ExtractionRecord>
    /** T15.5: the extractions waiting for a person, oldest first — `extraction:can_update`. */
    queue: (query?: ExtractionQuery) => Promise<Page<ExtractionQueueItem>>
    /**
     * W32 (FR-E50, D123): one page of a list the profile names (`list_paths`) — a read the
     * service logs with the purpose `extraction`. `limit` ≤ 1,000.
     */
    extractionRows: (documentId: string, extractionId: string, query: ExtractionRowsQuery) => Promise<ExtractionRows>
    /**
     * W32 (FR-E48, FR-E49): the organisation's column maps — a person's, `extraction:can_create`
     * to change and `extraction:can_view` to read; another org's map is `NOT_FOUND`. A change is
     * a new version; a version never changes; an archived map is chosen no more.
     */
    columnMaps: {
      list: (query?: ColumnMapQuery) => Promise<ColumnMap[]>
      /** One map with its versions, newest first. */
      get: (mapId: string) => Promise<ColumnMap>
      /** `201` the new map; an `import_key` this owner used before answers the existing one, `repeat: true`. */
      create: (input: ColumnMapInput) => Promise<ColumnMap>
      /** The map with its new newest version. */
      addVersion: (mapId: string, input: ColumnMapVersionInput) => Promise<ColumnMap>
      archive: (mapId: string) => Promise<ColumnMap>
      /**
       * D122: the engine's own reading of the file under an unsaved map — the first 50 rows,
       * the summary and the checks. Unmetered; nothing is kept. `202` with the job to follow
       * when it did not end inside `wait`.
       */
      preview: (input: MapPreviewInput) => Promise<MapPreviewAnswer>
    }
  }
  settings: {
    get: () => Promise<SettingsAnswer>
    update: (patch: SettingsPatch) => Promise<SettingsAnswer>
    /** W6: the statutory floor table with its citations (D22). */
    floors: () => Promise<RetentionFloor[]>
  }
  workspaces: {
    list: () => Promise<Workspace[]>
    get: (id: string) => Promise<Workspace>
    create: (input: WorkspaceInput) => Promise<Workspace>
    update: (id: string, input: Partial<WorkspaceInput>) => Promise<Workspace>
    /**
     * `move_to`: another active workspace's id, where its stored documents go.
     * Omitted while documents are filed, the service answers `CONFLICT` with
     * `details.document_count`, and the screen asks where they go. The default
     * workspace is never archived (`CONFLICT`, `default_workspace`).
     */
    archive: (id: string, input?: { move_to?: string }) => Promise<Workspace>
    members: {
      list: (workspaceId: string) => Promise<WorkspaceMember[]>
      put: (workspaceId: string, userId: string, kind: MemberKind) => Promise<WorkspaceMember>
      remove: (workspaceId: string, userId: string) => Promise<unknown>
    }
  }
}

/** A call that may carry a step-up assertion — the host's, for this one request (`X-StepUp-Assertion`). */
export interface StepUpOptions {
  stepUpAssertion?: string
}

interface Envelope<T> {
  success?: boolean
  data?: T
  meta?: Record<string, unknown>
  error?: { code?: string; message?: string; details?: Record<string, unknown> }
}

const ORG_HEADER = 'X-Fiveox-Org'
/** `app/platform/stepup.py`'s header. */
export const STEPUP_HEADER = 'X-StepUp-Assertion'
/** Plan §5.3: a share link's token and passcode travel as headers, never in a path or a query. */
export const SHARE_TOKEN_HEADER = 'X-Share-Token'
export const SHARE_PASSCODE_HEADER = 'X-Share-Passcode'

export function createProDocClient({ baseUrl, fetcher, orgId }: ProDocClientOptions): ProDocClient {
  const base = baseUrl.replace(/\/+$/, '')
  const currentOrg = () => (typeof orgId === 'function' ? orgId() : orgId)

  /**
   * **Headers as a plain object, never a `Headers` instance.** The shell's
   * `authed` merges `{ ...(init.headers ?? {}), authorization }`, and spreading
   * a `Headers` yields `{}` — every header but the bearer vanishes, and the
   * service's 400 reads on screen as "the permission service is down" (the
   * template app, PLATFORM_INTEGRATION §9.3).
   */
  function headersFor(init: { json?: boolean } = {}): Record<string, string> {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (init.json) headers['content-type'] = 'application/json'
    const org = currentOrg()
    if (!org) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    headers[ORG_HEADER] = org
    return headers
  }

  async function parse<T>(res: Response): Promise<{ data: T; meta: Record<string, unknown> }> {
    const body = (await res.json().catch(() => null)) as Envelope<T> | null
    if (!res.ok || !body?.success || body.data === undefined) {
      throw new ApiError(
        body?.error?.code ?? 'INTERNAL',
        body?.error?.message ?? 'Something went wrong.',
        res.status,
        body?.error?.details ?? {},
      )
    }
    return { data: body.data, meta: body.meta ?? {} }
  }

  async function send(url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetcher(url, init)
    } catch {
      // A thrown fetch: the network, CORS, or the service down. Status 0 is
      // what the access gate reads as "the authority did not answer".
      throw new ApiError('INTERNAL', 'Could not reach ProDoc.', 0)
    }
  }

  async function call<T>(
    path: string,
    init: { method?: string; body?: unknown; form?: FormData; stepUpAssertion?: string; shareToken?: string; noStore?: boolean } = {},
  ): Promise<{ data: T; meta: Record<string, unknown> }> {
    const headers = headersFor({ json: init.body !== undefined })
    // Only on the one request it was minted for, and only when there is one.
    if (init.stepUpAssertion) headers[STEPUP_HEADER] = init.stepUpAssertion
    // A link's token rides a header on a send (D26), never the path or the body.
    if (init.shareToken) headers[SHARE_TOKEN_HEADER] = init.shareToken
    const res = await send(`${base}/api/v1${path}`, {
      method: init.method ?? (init.body !== undefined || init.form ? 'POST' : 'GET'),
      headers,
      // Multipart leaves content-type to the browser, which sets the boundary.
      body: init.form ?? (init.body === undefined ? undefined : JSON.stringify(init.body)),
      // W23 (FR-52): a revealed value is never kept by the browser's HTTP cache either — the service says `no-store`, and so does the request.
      ...(init.noStore ? { cache: 'no-store' as const } : {}),
    })
    return parse<T>(res)
  }

  const data = async <T>(path: string, init?: Parameters<typeof call>[1]) => (await call<T>(path, init)).data

  /** A call answered `204 No Content` (W30's password save): a refusal throws as any other; an envelope is taken too. */
  async function callEmpty(path: string, init: { method: string; body?: unknown }): Promise<void> {
    const res = await send(`${base}/api/v1${path}`, {
      method: init.method,
      headers: headersFor({ json: init.body !== undefined }),
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
    if (res.status === 204) return
    const type = (res.headers.get('content-type') ?? '').toLowerCase()
    if (res.ok && !type.includes('application/json')) return
    const body = (await res
      .clone()
      .json()
      .catch(() => null)) as Envelope<unknown> | null
    if (res.ok && body?.success) return
    await parse<unknown>(res)
  }

  /** A list repeats its key (`tag=a&tag=b`); nothing empty is sent, so silence is the default. */
  function queryString(params: Record<string, string | number | string[] | null | undefined>): string {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) for (const v of value) search.append(key, v)
      else search.set(key, String(value))
    }
    const s = search.toString()
    return s ? `?${s}` : ''
  }

  /** A path the service answered (`/api/v1/downloads/…`) as an address the browser can navigate to. */
  const absolute = (url: string) => (/^https?:\/\//i.test(url) ? url : `${base}${url.startsWith('/') ? '' : '/'}${url}`)

  const id = (value: string) => encodeURIComponent(value)

  /**
   * A logged read (D4): the service writes the read row, **then** answers with
   * the five-minute presigned URL. Two answers are honoured:
   *
   * - **JSON `{url, file_name, media_type, expires_in}`** — asked for with
   *   `Accept: application/json`, which every call here sends. The screen opens
   *   or embeds the URL itself, with no header: a *followed* redirect would carry
   *   `X-Fiveox-Org` to the storage origin and force a preflight object storage
   *   does not answer.
   * - **302** to the URL, followed by `fetch` — the body is the bytes and the
   *   name is `Content-Disposition`'s. Works only where storage's CORS allows it.
   *
   * Either way a `text/html` answer is refused: nothing ProDoc stores is a web
   * page, and a fallen-through route saved as the file is the defect the
   * template app met four times on UAT before 2026-10-07.
   */
  async function readFile(path: string, purpose: ReadPurpose): Promise<FileHandle> {
    const res = await send(`${base}/api/v1${path}?purpose=${purpose}`, { headers: headersFor() })
    const type = (res.headers.get('content-type') ?? '').toLowerCase()
    if (type.startsWith('text/html')) {
      throw new ApiError('STORAGE_UNAVAILABLE', 'The file store answered with a web page, not the file.', res.status)
    }
    if (type.includes('application/json')) {
      const { data: body } = await parse<{ url: string; file_name?: string | null; media_type?: string | null; expires_in?: number | null }>(res)
      return {
        url: body.url,
        fileName: body.file_name ?? null,
        mediaType: body.media_type ?? null,
        expiresIn: typeof body.expires_in === 'number' ? body.expires_in : null,
        blob: async () => {
          let file: Response
          try {
            file = await fetch(body.url)
          } catch {
            throw new ApiError('STORAGE_UNAVAILABLE', 'The file store did not answer.', 0)
          }
          const got = (file.headers.get('content-type') ?? '').toLowerCase()
          if (!file.ok) throw new ApiError('STORAGE_UNAVAILABLE', `Download failed (${file.status}).`, file.status)
          if (got.startsWith('text/html')) {
            throw new ApiError('STORAGE_UNAVAILABLE', 'The file store answered with a web page, not the file.', file.status)
          }
          return file.blob()
        },
      }
    }
    if (!res.ok) {
      throw new ApiError(res.status === 404 ? 'NOT_FOUND' : 'STORAGE_UNAVAILABLE', `Download failed (${res.status}).`, res.status)
    }
    // The redirect was followed: the body is the file and `res.url` the presigned address.
    const blob = res.blob()
    return {
      url: res.redirected ? res.url : null,
      fileName: fileNameOf(res),
      mediaType: type || null,
      expiresIn: null,
      blob: () => blob,
    }
  }

  /**
   * A page the service drew (C-E12): the PNG itself, on the host's fetcher with
   * the org header — an `<img>` could carry neither, so the screen makes its own
   * address from the bytes. A refusal is the envelope, as anywhere; a web page
   * or anything else that is not an image is never taken for the page.
   */
  async function readPage(path: string): Promise<Blob> {
    const headers = headersFor()
    headers.accept = 'image/png, application/json'
    const res = await send(`${base}/api/v1${path}`, { headers })
    const type = (res.headers.get('content-type') ?? '').toLowerCase()
    if (res.ok && type.startsWith('image/')) return res.blob()
    // A refusal throws here with the service's code; a JSON success is not a page either.
    if (type.includes('application/json')) await parse<unknown>(res)
    if (!res.ok) throw new ApiError(res.status === 404 ? 'NOT_FOUND' : 'INTERNAL', `The page could not be drawn (${res.status}).`, res.status)
    throw new ApiError('INTERNAL', 'ProDoc answered with something other than the page.', res.status)
  }

  function appendFile(form: FormData, file: UploadFile): string {
    if (file instanceof File) {
      form.append('file', file)
      return file.name
    }
    form.append('file', file.file, file.file_name)
    return file.file_name
  }

  return {
    baseUrl: base,
    orgId: currentOrg,

    access: () => data<AccessAnswer>('/me/access'),

    documents: {
      options: () => data<DocumentOptions>('/documents/options'),

      list: async (query = {}) => {
        const { data: items, meta } = await call<ProDocDocument[]>(
          `/documents${queryString({
            q: query.q,
            workspace: query.workspace,
            folder: query.folder,
            // Only `true` is a question; `false` is the default, said by silence.
            include_subfolders: query.folder && query.include_subfolders ? 'true' : undefined,
            tag: query.tag?.length ? query.tag : undefined,
            owner_app: query.owner_app,
            uploaded_by: query.uploaded_by,
            document_type: query.document_type,
            document_date_from: query.document_date_from,
            document_date_to: query.document_date_to,
            uploaded_from: query.uploaded_from,
            uploaded_to: query.uploaded_to,
            status: query.status,
            retention: query.retention,
            review_state: query.review_state,
            exclude_own: query.exclude_own ? 'true' : undefined,
            view: query.view,
            reading: query.reading,
            password: query.password,
            personal_data: query.personal_data,
            sort: query.sort,
            dir: query.dir,
            source_ref: query.source_ref,
            limit: query.limit,
            offset: query.offset,
          })}`,
        )
        return {
          items: items.map(rowOf),
          total: Number(meta.total ?? items.length),
          limit: Number(meta.limit ?? query.limit ?? items.length),
          offset: Number(meta.offset ?? query.offset ?? 0),
          facets: meta.facets as DocumentFacets | undefined,
          people: peopleOf(meta),
          ...(typeof meta.four_eyes === 'boolean' ? { four_eyes: meta.four_eyes } : {}),
        }
      },

      get: async (documentId) => rowOf(await data<ProDocDocument>(`/documents/${id(documentId)}`)),

      upload: async (input) => {
        if (input.files.length === 0) throw new ApiError('VALIDATION_ERROR', 'Choose at least one file.', 400)
        // Joined by `/` below, so a name that holds one must be refused here, never split into two folders.
        const pathError = folderPathError(input.folder_path)
        if (pathError) throw new ApiError('VALIDATION_ERROR', pathError, 400, { field: 'folder_path' })
        const folderPath = folderPathNames(input.folder_path)
        const form = new FormData()
        const names = input.files.map((f) => appendFile(form, f))
        if (input.workspace_id) form.append('workspace_id', input.workspace_id)
        if (input.document_type) form.append('document_type', input.document_type)
        if (input.title) form.append('title', input.title)
        if (input.document_date) form.append('document_date', input.document_date)
        if (input.owner_app) form.append('owner_app', input.owner_app)
        if (input.source_ref) form.append('source_ref', input.source_ref)
        if (input.on_duplicate) form.append('on_duplicate', input.on_duplicate)
        if (input.duplicate_of) form.append('duplicate_of', input.duplicate_of)
        if (input.folder_id) form.append('folder_id', input.folder_id)
        if (folderPath.length > 0) form.append('folder_path', folderPath.join('/'))
        if (input.password) {
          // One `password` field: it can belong to one file only.
          if (input.files.length > 1) throw new ApiError('VALIDATION_ERROR', 'A password goes with one file at a time.', 400, { field: 'password' })
          form.append('password', input.password)
        }
        if (input.parse) {
          if (!input.parse.profile.trim()) throw new ApiError('VALIDATION_ERROR', 'Name what to read it as.', 400, { field: 'parse' })
          // The stored version is the one read, so the object names no version — and an upload asks for no password.
          const { profile, review, options, context } = input.parse
          form.append('parse', JSON.stringify(parseBody({ profile, review, options, context })))
        }
        const { data: body, meta } = await call<unknown>('/documents', { method: 'POST', form })
        return uploadResult(body, meta, names)
      },

      duplicates: (sha256) => data<ProDocDocument[]>(`/documents/duplicates${queryString({ sha256 })}`),

      discardDuplicate: (input) => data<unknown>('/documents/duplicates/discard', { method: 'POST', body: input }),

      update: (documentId, patch) => data<ProDocDocument>(`/documents/${id(documentId)}`, { method: 'PATCH', body: patch }),

      archive: (documentId) => data<ProDocDocument>(`/documents/${id(documentId)}/archive`, { method: 'POST' }),

      undoSplit: async (documentId) => {
        const { data: body, meta } = await call<ProDocDocument>(`/documents/${id(documentId)}/split/undo`, { method: 'POST' })
        const archived = Array.isArray(meta.archived) ? meta.archived.filter((x): x is string => typeof x === 'string') : []
        return { document: rowOf(body), archived }
      },

      restore: (documentId) => data<ProDocDocument>(`/documents/${id(documentId)}/restore`, { method: 'POST' }),

      file: (documentId, purpose = 'view') => readFile(`/documents/${id(documentId)}/file`, purpose),

      versions: {
        list: (documentId) => data<DocumentVersion[]>(`/documents/${id(documentId)}/versions`),
        add: async (documentId, input) => {
          const form = new FormData()
          appendFile(form, input.file)
          if (input.reason) form.append('reason', input.reason)
          if (input.password) form.append('password', input.password)
          if (input.parse) {
            if (!input.parse.profile.trim()) throw new ApiError('VALIDATION_ERROR', 'Name what to read it as.', 400, { field: 'parse' })
            // As on upload (D53): the stored version is the one read, so the object names no version and asks for no password.
            const { profile, review, options, context } = input.parse
            form.append('parse', JSON.stringify(parseBody({ profile, review, options, context })))
          }
          const { data: body, meta } = await call<unknown>(`/documents/${id(documentId)}/versions`, { method: 'POST', form })
          // The job rides the answer as an upload's does — beside the document, or in `meta.job`.
          const job = asRecord(asRecord(body)?.job) ?? asRecord(meta.job)
          const doc = rowOf(documentOf(body))
          return job ? { ...doc, job: jobOf(job) } : doc
        },
        file: (documentId, versionId, purpose = 'view') =>
          readFile(`/documents/${id(documentId)}/versions/${id(versionId)}/file`, purpose),
        pagePreview: async (documentId, versionId, page) => {
          if (!Number.isSafeInteger(page) || page < 1) throw new ApiError('VALIDATION_ERROR', 'Pages are numbered from 1.', 400, { field: 'page' })
          return readPage(`/documents/${id(documentId)}/versions/${id(versionId)}/pages/${page}`)
        },
      },

      savePassword: async (documentId, versionId, password) => {
        if (!password) throw new ApiError('VALIDATION_ERROR', 'Type the password.', 400, { field: 'password' })
        // `204` — nothing comes back, and the password is in this one body only.
        await callEmpty(`/documents/${id(documentId)}/versions/${id(versionId)}/password`, { method: 'PUT', body: { password } })
      },

      audit: async (documentId, page = {}) => {
        const { data: items, meta } = await call<unknown[]>(
          `/documents/${id(documentId)}/audit${queryString({ before: page.before, limit: page.limit })}`,
        )
        const next = meta.next_before ?? meta.next ?? meta.before ?? null
        return { items: items.map(auditEntryOf), next: typeof next === 'string' && next ? next : null }
      },

      review: {
        request: async (documentId) => documentOf(await data<unknown>(`/documents/${id(documentId)}/review/request`, { method: 'POST', body: {} })),
        approve: async (documentId, input = {}) =>
          documentOf(await data<unknown>(`/documents/${id(documentId)}/review/approve`, { method: 'POST', body: decisionBody(input) })),
        reject: async (documentId, input = {}) =>
          documentOf(await data<unknown>(`/documents/${id(documentId)}/review/reject`, { method: 'POST', body: decisionBody(input) })),
      },

      setTags: async (documentId, input) =>
        documentOf(
          await data<unknown>(`/documents/${id(documentId)}/tags`, {
            method: 'PUT',
            body: { tag_ids: input.tag_ids, new_tags: (input.new_tags ?? []).map((t) => ({ name: t.name.trim(), color: t.color ?? 'neutral' })) },
          }),
        ),

      related: async (documentId) => relatedOf(await data<unknown>(`/documents/${id(documentId)}/related`)),

      links: {
        add: (documentId, relatedId) =>
          data<unknown>(`/documents/${id(documentId)}/links`, { method: 'POST', body: { related_document_id: relatedId } }),
        remove: (documentId, relatedId) => data<unknown>(`/documents/${id(documentId)}/links/${id(relatedId)}`, { method: 'DELETE' }),
      },

      bulk: async (input) => {
        if (input.document_ids.length === 0) throw new ApiError('VALIDATION_ERROR', 'Select at least one document.', 400)
        const body: Record<string, unknown> = { action: input.action, document_ids: input.document_ids }
        if (input.action === 'tag' || input.action === 'untag') body.tag_ids = input.tag_ids ?? []
        if (input.action === 'move_folder') body.folder_id = input.folder_id ?? null
        if (input.action === 'move_workspace') body.workspace_id = input.workspace_id
        const { data: answer, meta } = await call<unknown>('/documents/bulk', { method: 'POST', body })
        return bulkResult(answer, meta, input.document_ids)
      },

      zip: async (documentIds) => {
        const ticket = await data<ZipTicket>('/documents/zip', { method: 'POST', body: { document_ids: documentIds } })
        return { ...ticket, url: absolute(ticket.url) }
      },

      setRetention: async (documentId, input, opts = {}) =>
        documentOf(
          await data<unknown>(`/documents/${id(documentId)}/retention`, {
            method: 'PUT',
            body: { retain_until: input.retain_until, reason: input.reason.trim() },
            stepUpAssertion: opts.stepUpAssertion,
          }),
        ),

      shares: async (documentId) => sharesOf(await data<unknown>(`/documents/${id(documentId)}/shares`)),

      personalData: {
        list: async (documentId) => {
          const { data: body, meta } = await call<unknown>(`/documents/${id(documentId)}/personal-data`)
          return findingsOf(body, meta)
        },
        reveal: async (documentId, findingId) =>
          revealedValueOf(
            await data<unknown>(`/documents/${id(documentId)}/personal-data/${id(findingId)}/reveal`, { method: 'POST', body: {}, noStore: true }),
          ),
      },
    },

    folders: {
      list: (query) => data<Folder[]>(`/folders${queryString({ workspace_id: query.workspace_id, parent_id: query.parent_id })}`),
      tree: (workspaceId) => data<Folder[]>(`/folders${queryString({ workspace_id: workspaceId, tree: 'true' })}`),
      get: async (folderId) => folderDetailOf(await data<unknown>(`/folders/${id(folderId)}`)),
      create: (input) =>
        data<Folder>('/folders', { method: 'POST', body: { workspace_id: input.workspace_id, parent_id: input.parent_id ?? null, name: input.name.trim() } }),
      ensurePaths: async (input) => {
        const answer = await data<unknown>('/folders/paths', {
          method: 'POST',
          body: { workspace_id: input.workspace_id, parent_id: input.parent_id ?? null, paths: input.paths },
        })
        return pathsOf(answer)
      },
      update: (folderId, patch) => data<Folder>(`/folders/${id(folderId)}`, { method: 'PATCH', body: patch }),
      archive: (folderId) => data<Folder>(`/folders/${id(folderId)}/archive`, { method: 'POST' }),
      shared: async () => {
        const answer = await data<unknown>(`/folders${queryString({ shared: 'true' })}`)
        return (Array.isArray(answer) ? answer : []) as Folder[]
      },
    },

    shares: {
      create: async (input, opts = {}) => {
        const answer = await data<unknown>('/shares', { method: 'POST', body: shareBody(input), stepUpAssertion: opts.stepUpAssertion })
        return createdShareOf(answer)
      },
      list: async (query = {}) =>
        sharesOf(
          await data<unknown>(
            `/shares${queryString({ document_id: query.document_id, folder_id: query.folder_id, mine: query.mine ? 'true' : undefined })}`,
          ),
        ),
      received: async () => sharesOf(await data<unknown>('/shares/received')),
      revoke: async (shareId) => shareOf(await data<unknown>(`/shares/${id(shareId)}/revoke`, { method: 'POST', body: {} })),
      send: async (shareId, input) => {
        const message = input.message?.trim()
        const answer = await data<unknown>(`/shares/${id(shareId)}/send`, {
          method: 'POST',
          body: {
            channel: input.channel,
            recipients: input.recipients.map((r) => r.trim()).filter(Boolean),
            ...(message ? { message } : {}),
          },
          shareToken: input.token,
        })
        const r = asRecord(answer) ?? {}
        return {
          sent_as: typeof r.sent_as === 'string' ? r.sent_as : null,
          channel: typeof r.channel === 'string' ? r.channel : input.channel,
          sent: num(r.sent),
          failed: num(r.failed),
        }
      },
      channels: async () => channelsOf(await data<unknown>('/sharing/channels')),
    },

    erasure: {
      create: async (input, opts = {}) => {
        const reason = input.reason?.trim()
        const answer = await data<unknown>('/erasure-requests', {
          method: 'POST',
          body: { reference: input.reference.trim(), ...(reason ? { reason } : {}), document_ids: input.document_ids },
          stepUpAssertion: opts.stepUpAssertion,
        })
        return erasureOf(answer)
      },
      list: async () => {
        const answer = await data<unknown>('/erasure-requests')
        return (Array.isArray(answer) ? answer : []).map(erasureOf)
      },
      get: async (requestId) => erasureOf(await data<unknown>(`/erasure-requests/${id(requestId)}`)),
    },

    tags: {
      list: () => data<Tag[]>('/tags'),
      create: (input) => data<Tag>('/tags', { method: 'POST', body: { name: input.name.trim(), color: input.color ?? 'neutral' } }),
      update: (tagId, input) =>
        data<Tag>(`/tags/${id(tagId)}`, { method: 'PATCH', body: { ...input, ...(input.name !== undefined ? { name: input.name.trim() } : {}) } }),
      merge: (tagId, intoId) => data<unknown>(`/tags/${id(tagId)}/merge`, { method: 'POST', body: { into_tag_id: intoId } }),
      remove: (tagId) => data<unknown>(`/tags/${id(tagId)}`, { method: 'DELETE' }),
    },

    summary: async (period = {}) => {
      const { data: body, meta } = await call<unknown>(`/summary${queryString({ from: period.from, to: period.to })}`)
      return summaryOf(body, meta)
    },

    parsing: {
      profiles: async () => {
        const { data: body, meta } = await call<unknown>('/profiles')
        return profileListOf(body, meta)
      },
      parse: async (documentId, input) => {
        if (!input.profile.trim()) throw new ApiError('VALIDATION_ERROR', 'Name what to read it as.', 400, { field: 'profile' })
        // FR-E5: a re-read is asked for with a reason, which the service records with it.
        if (input.force && !(input.reason ?? '').trim()) throw new ApiError('VALIDATION_ERROR', 'Say why it is read again.', 400, { field: 'reason' })
        const { data: body, meta } = await call<unknown>(`/documents/${id(documentId)}/parse`, { method: 'POST', body: parseBody(input) })
        return parseAnswerOf(body, meta)
      },
      job: async (jobId) => jobOf(await data<unknown>(`/jobs/${id(jobId)}`)),
      survey: async (documentId, input = {}) => {
        const body: Record<string, unknown> = {}
        if (input.version_id) body.version_id = input.version_id
        if (input.password) body.password = input.password
        if (input.wait !== undefined) body.wait = input.wait
        return surveyAnswerOf(await data<unknown>(`/documents/${id(documentId)}/survey`, { method: 'POST', body }))
      },
      extractions: async (documentId) => {
        const answer = await data<unknown>(`/documents/${id(documentId)}/extractions`)
        return (Array.isArray(answer) ? answer : []).map(extractionRecordOf)
      },
      extraction: async (documentId, extractionId, options = {}) => {
        // W23: `meta.masked_paths` names the fields the service masked for this person.
        // W32 (D123): `rows=summary` swaps each list the profile names for its count.
        const { data: body, meta } = await call<unknown>(`/documents/${id(documentId)}/extractions/${id(extractionId)}${queryString({ rows: options.rows })}`)
        return extractionOf(body, meta)
      },
      reveal: async (documentId, extractionId, fieldPath) => {
        if (!fieldPath.trim()) throw new ApiError('VALIDATION_ERROR', 'Name the field to reveal.', 400, { field: 'field_path' })
        const answer = await data<unknown>(`/documents/${id(documentId)}/extractions/${id(extractionId)}/reveal`, {
          method: 'POST',
          body: { field_path: fieldPath },
          noStore: true,
        })
        return revealedFieldOf(answer, fieldPath)
      },
      correct: async (documentId, extractionId, input) => {
        if (input.corrections.length === 0) throw new ApiError('VALIDATION_ERROR', 'Correct at least one field.', 400, { field: 'corrections' })
        const reason = input.reason?.trim()
        const answer = await data<unknown>(`/documents/${id(documentId)}/extractions/${id(extractionId)}/corrections`, {
          method: 'POST',
          body: { corrections: input.corrections.map((c) => ({ field_path: c.field_path, value: c.value })), ...(reason ? { reason } : {}) },
        })
        return correctionAnswerOf(answer)
      },
      accept: async (documentId, extractionId, input = {}) => {
        const comment = input.comment?.trim()
        return extractionRecordOf(
          await data<unknown>(`/documents/${id(documentId)}/extractions/${id(extractionId)}/accept`, { method: 'POST', body: comment ? { comment } : {} }),
        )
      },
      reject: async (documentId, extractionId, input = {}) => {
        const comment = input.comment?.trim()
        return extractionRecordOf(
          await data<unknown>(`/documents/${id(documentId)}/extractions/${id(extractionId)}/reject`, { method: 'POST', body: comment ? { comment } : {} }),
        )
      },
      queue: async (query = {}) => {
        const { data: items, meta } = await call<unknown[]>(
          `/extractions${queryString({ state: query.state ?? 'needs_review', profile: query.profile, limit: query.limit, offset: query.offset })}`,
        )
        const rows = (Array.isArray(items) ? items : []).map(queueItemOf)
        return {
          items: rows,
          total: Number(meta.total ?? rows.length),
          limit: Number(meta.limit ?? query.limit ?? rows.length),
          offset: Number(meta.offset ?? query.offset ?? 0),
        }
      },
      extractionRows: async (documentId, extractionId, query) => {
        if (!query.path.trim()) throw new ApiError('VALIDATION_ERROR', 'Name the list to read.', 400, { field: 'path' })
        if (query.limit !== undefined && (!Number.isInteger(query.limit) || query.limit < 1 || query.limit > MAX_ROWS_PAGE)) {
          throw new ApiError('VALIDATION_ERROR', `Up to ${MAX_ROWS_PAGE} rows a page.`, 400, { field: 'limit' })
        }
        if (query.offset !== undefined && (!Number.isInteger(query.offset) || query.offset < 0)) {
          throw new ApiError('VALIDATION_ERROR', 'A row to start from, 0 or more.', 400, { field: 'offset' })
        }
        const answer = await data<unknown>(
          `/documents/${id(documentId)}/extractions/${id(extractionId)}/rows${queryString({ path: query.path, offset: query.offset, limit: query.limit })}`,
        )
        return extractionRowsOf(answer, query)
      },
      columnMaps: {
        list: async (query = {}) => {
          const archived = query.archived === undefined ? undefined : String(query.archived)
          const answer = await data<unknown>(`/column-maps${queryString({ profile: query.profile, scope: query.scope, archived })}`)
          return (Array.isArray(answer) ? answer : []).map((m) => columnMapOf(m))
        },
        get: async (mapId) => columnMapOf(await data<unknown>(`/column-maps/${id(mapId)}`)),
        create: async (input) => {
          const name = input.name.trim()
          const scope = input.scope.trim()
          if (!name) throw new ApiError('VALIDATION_ERROR', 'Name the map.', 400, { field: 'name' })
          if (name.length > MAX_MAP_NAME) throw new ApiError('VALIDATION_ERROR', `Up to ${MAX_MAP_NAME} characters.`, 400, { field: 'name' })
          if (!scope || scope.length > MAX_MAP_SCOPE) throw new ApiError('VALIDATION_ERROR', 'Say whose map it is: the organisation’s or a client’s.', 400, { field: 'scope' })
          const body: Record<string, unknown> = { profile: input.profile, scope, name, spec: mapSpecBody(input.spec) }
          if (input.fingerprint) body.fingerprint = input.fingerprint
          if (input.import_key) body.import_key = input.import_key
          const { data: answer, meta } = await call<unknown>('/column-maps', { method: 'POST', body })
          return columnMapOf(answer, meta)
        },
        addVersion: async (mapId, input) => {
          const body: Record<string, unknown> = { spec: mapSpecBody(input.spec) }
          if (input.fingerprint) body.fingerprint = input.fingerprint
          return columnMapOf(await data<unknown>(`/column-maps/${id(mapId)}/versions`, { method: 'POST', body }))
        },
        archive: async (mapId) => columnMapOf(await data<unknown>(`/column-maps/${id(mapId)}/archive`, { method: 'POST', body: {} })),
        preview: async (input) => {
          const body: Record<string, unknown> = { document_id: input.document_id, profile: input.profile, spec: mapSpecBody(input.spec) }
          if (input.version_id) body.version_id = input.version_id
          if (input.wait !== undefined) body.wait = input.wait
          return mapPreviewAnswerOf(await data<unknown>('/column-maps/preview', { method: 'POST', body }))
        },
      },
    },

    settings: {
      get: async () => settingsAnswerOf(await data<unknown>('/settings')),
      update: async (patch) => settingsAnswerOf(await data<unknown>('/settings', { method: 'PATCH', body: patch })),
      floors: async () => floorsOf(await data<unknown>('/settings/retention-floors')),
    },

    workspaces: {
      list: () => data<Workspace[]>('/workspaces'),
      get: (workspaceId) => data<Workspace>(`/workspaces/${id(workspaceId)}`),
      create: (input) => data<Workspace>('/workspaces', { method: 'POST', body: input }),
      update: (workspaceId, input) => data<Workspace>(`/workspaces/${id(workspaceId)}`, { method: 'PATCH', body: input }),
      archive: (workspaceId, input = {}) =>
        data<Workspace>(`/workspaces/${id(workspaceId)}/archive`, {
          method: 'POST',
          body: input.move_to === undefined ? {} : { move_to: input.move_to },
        }),
      members: {
        list: (workspaceId) => data<WorkspaceMember[]>(`/workspaces/${id(workspaceId)}/members`),
        put: (workspaceId, userId, kind) =>
          data<WorkspaceMember>(`/workspaces/${id(workspaceId)}/members/${id(userId)}`, { method: 'PUT', body: { kind } }),
        remove: (workspaceId, userId) => data<unknown>(`/workspaces/${id(workspaceId)}/members/${id(userId)}`, { method: 'DELETE' }),
      },
    },
  }
}

/* ------------------------------------------------ a share link's reader */

export interface ShareLinkClientOptions {
  /** The service's origin, as for `createProDocClient`. */
  baseUrl: string
  /**
   * How to reach it. A reader with no account: the browser's `fetch`. A
   * signed-in reader of an `access: org` link: a fetcher that adds their
   * bearer — and that must **not** replay a passcode refusal (each replay
   * would count as another wrong try towards the lockout).
   */
  fetcher?: Fetcher
}

/** The token, and the passcode when the reader gave one — both travel as headers only. */
export interface ShareLinkKey {
  token: string
  passcode?: string | null
}

export interface ShareLinkClient {
  /** `GET /public/share`: what the link reaches. Refusals: `passcode_required`, `passcode_wrong`, `sign_in_required` (401), `locked` (403), `NOT_FOUND`. */
  open: (key: ShareLinkKey) => Promise<SharedBundle>
  /** `GET /public/share/folders/{id}`: one folder inside the shared subtree. */
  folder: (key: ShareLinkKey, folderId: string) => Promise<SharedFolderListing>
  /** `GET /public/share/documents/{id}/file`: the read is logged (`link`, `share`), then a five-minute URL. */
  file: (key: ShareLinkKey, documentId: string) => Promise<FileHandle>
}

/**
 * The client a share link's page reads with (plan §5.3) — **no organisation
 * header and no session of its own**: the token names the share. The token
 * rides in `X-Share-Token` and the passcode in `X-Share-Passcode` on every
 * call; **neither ever goes into a path or a query** — the link's page address
 * (`/s/<token>`) is the only place the token appears, and the host's edge does
 * not log it (D35). The client keeps neither.
 */
export function createShareLinkClient({ baseUrl, fetcher }: ShareLinkClientOptions): ShareLinkClient {
  const base = baseUrl.replace(/\/+$/, '')
  const reach: Fetcher = fetcher ?? ((url, init) => fetch(url, init))
  const id = (value: string) => encodeURIComponent(value)

  async function call<T>(path: string, key: ShareLinkKey): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json', [SHARE_TOKEN_HEADER]: key.token }
    if (key.passcode) headers[SHARE_PASSCODE_HEADER] = key.passcode
    let res: Response
    try {
      res = await reach(`${base}/api/v1/public/share${path}`, { headers })
    } catch {
      throw new ApiError('INTERNAL', 'Could not reach ProDoc.', 0)
    }
    const body = (await res.json().catch(() => null)) as Envelope<T> | null
    if (!res.ok || !body?.success || body.data === undefined) {
      throw new ApiError(body?.error?.code ?? 'INTERNAL', body?.error?.message ?? 'Something went wrong.', res.status, body?.error?.details ?? {})
    }
    return body.data
  }

  return {
    open: async (key) => bundleOf(await call<unknown>('', key)),
    folder: async (key, folderId) => folderListingOf(await call<unknown>(`/folders/${id(folderId)}`, key)),
    file: async (key, documentId) => {
      const body = asRecord(await call<unknown>(`/documents/${id(documentId)}/file`, key)) ?? {}
      const url = typeof body.url === 'string' ? body.url : null
      return {
        url,
        fileName: typeof body.file_name === 'string' ? body.file_name : null,
        mediaType: typeof body.media_type === 'string' ? body.media_type : null,
        expiresIn: typeof body.expires_in === 'number' ? body.expires_in : null,
        blob: async () => {
          if (!url) throw new ApiError('STORAGE_UNAVAILABLE', 'The file store gave no address.', 0)
          let file: Response
          try {
            // The presigned URL itself, with no header: nothing of the share goes to the store.
            file = await fetch(url)
          } catch {
            throw new ApiError('STORAGE_UNAVAILABLE', 'The file store did not answer.', 0)
          }
          if (!file.ok) throw new ApiError('STORAGE_UNAVAILABLE', `Download failed (${file.status}).`, file.status)
          if ((file.headers.get('content-type') ?? '').toLowerCase().startsWith('text/html')) {
            throw new ApiError('STORAGE_UNAVAILABLE', 'The file store answered with a web page, not the file.', file.status)
          }
          return file.blob()
        },
      }
    },
  }
}

/* ------------------------------------------------------- answer shaping */

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

const str = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

/** `POST /shares`'s body: only what the kind takes, nothing empty — the service refuses what it does not expect. */
function shareBody(input: ShareInput): Record<string, unknown> {
  const body: Record<string, unknown> = { kind: input.kind }
  if (input.document_ids?.length) body.document_ids = input.document_ids
  if (input.folder_ids?.length) body.folder_ids = input.folder_ids
  if (input.kind === 'member') body.recipient_user_ids = input.recipient_user_ids ?? []
  if (input.kind === 'link') {
    body.access = input.access ?? 'org'
    if (input.expires_in_days !== undefined) body.expires_in_days = input.expires_in_days
    if (input.passcode) body.passcode = input.passcode
  }
  if (input.kind === 'app') body.recipient_app = input.recipient_app
  return body
}

/** One share, whatever names the service gave its parts — items flat (`document_ids` / `folder_ids`) or as rows. */
export function shareOf(raw: unknown): Share {
  const r = asRecord(raw) ?? {}
  const items: ShareItem[] = []
  if (Array.isArray(r.items)) {
    for (const it of r.items) {
      const row = asRecord(it) ?? {}
      const document_id = str(row.document_id)
      const folder_id = str(row.folder_id)
      if (document_id || folder_id) items.push({ document_id, folder_id })
    }
  }
  for (const d of idList(r.document_ids)) if (!items.some((i) => i.document_id === d)) items.push({ document_id: d, folder_id: null })
  for (const f of idList(r.folder_ids)) if (!items.some((i) => i.folder_id === f)) items.push({ document_id: null, folder_id: f })
  const kind = r.kind === 'link' || r.kind === 'app' ? r.kind : 'member'
  return {
    id: str(r.id) ?? '',
    kind,
    access: r.access === 'anyone' || r.access === 'org' ? r.access : null,
    recipient_user_id: str(r.recipient_user_id),
    recipient_app: str(r.recipient_app),
    items,
    expires_at: str(r.expires_at),
    revoked_at: str(r.revoked_at),
    revoked_by: str(r.revoked_by),
    created_by: str(r.created_by),
    created_at: str(r.created_at),
    last_opened_at: str(r.last_opened_at),
    // `passcode_set` is the service's word (`app/domain/sharing.py`, `share_out`).
    has_passcode: Boolean(r.passcode_set ?? r.has_passcode ?? r.passcode_protected),
    locked_until: str(r.locked_until),
    sent_as: str(r.sent_as),
    sent_channel: str(r.sent_channel),
    sent_at: str(r.sent_at),
    via_folder_id: str(r.via_folder_id) ?? str(asRecord(r.via)?.folder_id),
  }
}

/** A list of shares — bare, or under `shares` / `items`. */
export function sharesOf(body: unknown): Share[] {
  const raw = Array.isArray(body) ? body : (asRecord(body)?.shares ?? asRecord(body)?.items)
  return (Array.isArray(raw) ? raw : []).map(shareOf)
}

/**
 * `POST /shares`' answer: the share — or one per member — and a link's token,
 * read under every name it might come back as (`token`, `link_token`, the
 * share's own `token`), with the service's `url` when it composes one.
 */
export function createdShareOf(body: unknown): CreatedShare {
  const r = asRecord(body) ?? {}
  const list = Array.isArray(body) ? body : Array.isArray(r.shares) ? r.shares : [asRecord(r.share) ?? r]
  const first = asRecord(list[0]) ?? {}
  return {
    shares: list.map(shareOf).filter((s) => s.id),
    token: str(r.token) ?? str(r.link_token) ?? str(first.token) ?? null,
    url: str(r.url) ?? str(r.share_url) ?? str(r.link) ?? str(first.url) ?? str(first.share_url) ?? null,
  }
}

/** `GET /sharing/channels`: a list of `{channel | family, …}`, or a map family → state. */
export function channelsOf(body: unknown): ChannelState[] {
  const shape = (raw: unknown, family?: string): ChannelState | null => {
    const r = asRecord(raw)
    if (!r) return null
    const channel = str(r.channel) ?? str(r.family) ?? family ?? null
    if (!channel) return null
    return { channel, active: Boolean(r.active), provider: str(r.provider), sends_as: str(r.sends_as), reason: str(r.reason) }
  }
  const record = asRecord(body)
  const raw = Array.isArray(body) ? body : Array.isArray(record?.channels) ? (record!.channels as unknown[]) : null
  const found = raw
    ? raw.map((r) => shape(r))
    : Object.entries(asRecord(record?.channels) ?? record ?? {}).map(([family, state]) => shape(state, family))
  return found.filter((c): c is ChannelState => Boolean(c))
}

/** One erasure request, its items read whether nested under `items` or `documents`. */
export function erasureOf(raw: unknown): ErasureRequest {
  const r = asRecord(raw) ?? {}
  const request = asRecord(r.request) ?? r
  const rawItems = Array.isArray(r.items) ? r.items : Array.isArray(request.items) ? request.items : Array.isArray(r.documents) ? r.documents : []
  const items: ErasureItem[] = rawItems.map((it) => {
    const row = asRecord(it) ?? {}
    const rules = Array.isArray(row.floor_rules) ? row.floor_rules : Array.isArray(row.citations) ? row.citations : []
    return {
      document_id: str(row.document_id) ?? str(row.id) ?? '',
      outcome: str(row.outcome) ?? 'erased',
      floor_until: str(row.floor_until),
      floor_rules: rules
        .map((x) => {
          if (typeof x === 'string') return { rule: x, citation: x }
          const f = asRecord(x) ?? {}
          return { rule: str(f.rule) ?? str(f.citation) ?? '', citation: str(f.citation) ?? str(f.rule) ?? '', until: str(f.until) }
        })
        .filter((f) => f.citation),
      erased_at: str(row.erased_at),
    }
  })
  const count = (key: string, fallback: number) => num(request[key]) ?? fallback
  return {
    id: str(request.id) ?? '',
    reference: str(request.reference) ?? '',
    reason: str(request.reason),
    requested_by: str(request.requested_by),
    document_count: count('document_count', items.length),
    erased_count: count('erased_count', items.filter((i) => i.outcome === 'erased').length),
    held_count: count('held_count', items.filter((i) => i.outcome === 'held_by_floor').length),
    completed_at: str(request.completed_at),
    created_at: str(request.created_at),
    items,
  }
}

/**
 * The floor table, from `GET /settings/retention-floors` or the settings
 * answer: a list, or `{floors: […]}`, or a map rule → floor; each floor's
 * types as `document_types` or `types`, its months as `months` or
 * `months_after_fy_end`.
 */
export function floorsOf(body: unknown): RetentionFloor[] {
  const record = asRecord(body)
  const types = Array.isArray(record?.types) ? (record!.types as unknown[]).map((t) => asRecord(t)).filter((t): t is Record<string, unknown> => Boolean(t)) : []
  const raw: [string | null, unknown][] = Array.isArray(body)
    ? body.map((f) => [null, f])
    : Array.isArray(record?.floors)
      ? (record!.floors as unknown[]).map((f) => [null, f])
      : Object.entries(asRecord(record?.floors) ?? record ?? {})
  const out: RetentionFloor[] = []
  for (const [key, value] of raw) {
    const f = asRecord(value)
    if (!f) continue
    const months = num(f.months) ?? num(f.months_after_fy_end) ?? num(f.floor_months)
    // The service keys a floor by `code` and names it in `rule` ("CGST Act 2017 §36").
    const code = str(f.code) ?? str(f.key)
    const rule = code ?? str(f.rule) ?? key
    if (!rule || months === null) continue
    const named = types.filter((t) => idList(t.floors).includes(rule)).map((t) => str(t.document_type)).filter((t): t is string => Boolean(t))
    out.push({
      rule,
      label: (code ? str(f.rule) : null) ?? str(f.label) ?? str(f.name) ?? str(f.act) ?? rule,
      citation: str(f.citation) ?? str(f.source) ?? '',
      months,
      anchor: str(f.anchor) ?? (num(f.months_after_fy_end) !== null ? 'fy_end' : null),
      document_types: [...new Set([...idList(f.document_types), ...(Array.isArray(f.types) ? idList(f.types) : []), ...named])],
      verified: typeof f.verified === 'boolean' ? f.verified : null,
    })
  }
  // A type no statute names keeps the organisation's minimum, counted from the upload (spec §5).
  const minimum = types.filter((t) => idList(t.floors).length === 0 && t.basis === 'after_upload')
  if (minimum.length) {
    out.push({
      rule: 'org_minimum',
      label: 'Organisation minimum',
      citation: 'No statute names this type — at least a year after upload (spec §5)',
      months: num(minimum[0]!.minimum_months) ?? num(record?.org_minimum_months) ?? 12,
      anchor: 'upload',
      document_types: minimum.map((t) => str(t.document_type)).filter((t): t is string => Boolean(t)),
      verified: null,
    })
  }
  return out
}

/** The settings answer, its W6 floor table read wherever it sits. */
export function settingsAnswerOf(body: unknown): SettingsAnswer {
  const r = (asRecord(body) ?? {}) as unknown as SettingsAnswer & { retention_floors?: unknown }
  const rawFloors = r.floors ?? r.retention_floors
  return rawFloors === undefined ? r : { ...r, floors: floorsOf(rawFloors) }
}

function sharedDocumentOf(raw: unknown): SharedDocument | null {
  const r = asRecord(raw)
  const docId = str(r?.id) ?? str(r?.document_id)
  if (!r || !docId) return null
  const fileName = str(r.file_name) ?? str(r.name) ?? str(r.title) ?? ''
  return {
    id: docId,
    title: str(r.title) ?? fileName,
    file_name: fileName,
    size_bytes: num(r.size_bytes) ?? 0,
    media_type: str(r.media_type),
    document_type: str(r.document_type),
  }
}

function sharedFolderOf(raw: unknown): SharedFolder | null {
  const r = asRecord(raw)
  const folderId = str(r?.id) ?? str(r?.folder_id)
  if (!r || !folderId) return null
  // The bundle's folders carry their top level (`app/api/public.py`): count it when no count is sent.
  return {
    id: folderId,
    name: str(r.name) ?? '',
    document_count: num(r.document_count) ?? (Array.isArray(r.documents) ? r.documents.length : null),
    folder_count: num(r.folder_count) ?? num(r.child_count) ?? (Array.isArray(r.folders) ? r.folders.length : null),
  }
}

/** The link's bundle: documents and folders as two lists, or as one `items` list of `{kind, …}`. */
export function bundleOf(body: unknown): SharedBundle {
  const r = asRecord(body) ?? {}
  const share = asRecord(r.share) ?? r
  const documents: SharedDocument[] = []
  const folders: SharedFolder[] = []
  const take = (raw: unknown, kind?: string) => {
    const row = asRecord(raw)
    if (!row) return
    const k = kind ?? str(row.kind) ?? str(row.type) ?? (row.folder_id && !row.document_id ? 'folder' : 'document')
    const inner = asRecord(row.document) ?? asRecord(row.folder) ?? row
    if (k === 'folder') {
      const f = sharedFolderOf(inner)
      if (f) folders.push(f)
    } else {
      const d = sharedDocumentOf(inner)
      if (d) documents.push(d)
    }
  }
  if (Array.isArray(r.documents)) for (const d of r.documents) take(d, 'document')
  if (Array.isArray(r.folders)) for (const f of r.folders) take(f, 'folder')
  if (Array.isArray(r.items)) for (const i of r.items) take(i)
  return {
    expires_at: str(share.expires_at),
    access: share.access === 'anyone' || share.access === 'org' ? share.access : null,
    org_name: str(r.org_name) ?? str(asRecord(r.org)?.display_name) ?? str(asRecord(r.org)?.name),
    shared_by: str(r.shared_by) ?? str(r.shared_by_name) ?? str(asRecord(r.shared_by)?.display_name),
    documents,
    folders,
  }
}

/** A folder inside the link: the folder (flat or under `folder`), its trail, and what is in it. */
export function folderListingOf(body: unknown): SharedFolderListing {
  const r = asRecord(body) ?? {}
  const folder = sharedFolderOf(asRecord(r.folder) ?? r) ?? { id: '', name: '', document_count: null, folder_count: null }
  const crumbs = Array.isArray(r.breadcrumb) ? r.breadcrumb : []
  const lists = bundleOf({ documents: r.documents, folders: r.folders ?? r.children, items: r.items })
  return {
    folder,
    breadcrumb: crumbs
      .map((c) => asRecord(c))
      .filter((c): c is Record<string, unknown> => Boolean(c && typeof c.id === 'string'))
      .map((c) => ({ id: c.id as string, name: str(c.name) ?? '' })),
    folders: lists.folders,
    documents: lists.documents,
  }
}

const idList = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((d): d is string => typeof d === 'string') : typeof raw === 'string' ? [raw] : []

/** A decision's body: the comment trimmed, and left out when there is none — "no comment" is a decision too. */
function decisionBody(input: ReviewDecisionInput): { comment?: string; version_id?: string } {
  const comment = input.comment?.trim()
  return { ...(comment ? { comment } : {}), ...(input.version_id ? { version_id: input.version_id } : {}) }
}

/**
 * A document as the service answered it, with W22's `reading` and `password`
 * and W23's `personal_data` read defensively (`readingOf`, `passwordStateOf`,
 * `personalDataOf`) — and passed on exactly as it came when it carries none of
 * them, as an older service's does.
 */
export function rowOf<T extends ProDocDocument>(raw: T): T {
  const r = asRecord(raw)
  const split = r !== null && ('split_state' in r || 'split_pages' in r || 'split_from' in r || 'split_parts' in r)
  if (!r || !('reading' in r || 'password' in r || 'personal_data' in r || split)) return raw
  const next = { ...raw }
  if ('reading' in r) next.reading = readingOf(r.reading)
  if ('password' in r) next.password = passwordStateOf(r.password)
  if ('personal_data' in r) next.personal_data = personalDataOf(r.personal_data)
  // W37 (C8): the split's keys, each checked — a shape that does not hold together reads as none.
  if ('split_state' in r) next.split_state = splitStateOf(r.split_state)
  if ('split_pages' in r) next.split_pages = splitPagesOf(r.split_pages)
  if ('split_from' in r) next.split_from = splitFromOf(r.split_from)
  if ('split_parts' in r) next.split_parts = splitPartsOf(r.split_parts)
  return next
}

/** A version add or a review decision may answer the document, or `{document, …}`; the screen wants the document. */
function documentOf(body: unknown): ProDocDocument {
  const record = asRecord(body)
  const inner = record ? asRecord(record.document) : null
  return (inner ?? record ?? {}) as unknown as ProDocDocument
}

/**
 * One line of the history, whichever way the service names its actor (W9,
 * FR-40): nested — `actor: {kind, user_id, service, display_name, party}` — or
 * flat beside the row — `actor_kind`, `actor_user_id`, `actor_service`,
 * `actor_party` — both read into `actor`, the nested value first. A row that
 * names no actor either way is passed on as it came.
 */
export function auditEntryOf(raw: unknown): AuditEntry {
  const r = asRecord(raw) ?? {}
  const nested = asRecord(r.actor)
  const flat = ['actor_kind', 'actor_user_id', 'actor_service', 'actor_party'].some((key) => key in r)
  if (!nested && !flat) return r as unknown as AuditEntry
  const actor: AuditActor = {
    kind: str(nested?.kind) ?? str(r.actor_kind) ?? '',
    user_id: str(nested?.user_id) ?? str(r.actor_user_id),
    service: str(nested?.service) ?? str(r.actor_service),
    display_name: str(nested?.display_name) ?? str(r.actor_display_name),
    party: str(nested?.party) ?? str(nested?.actor_party) ?? str(r.actor_party),
  }
  return { ...(r as unknown as AuditEntry), actor }
}

/** A per-file refusal as the error a screen already knows how to read. */
function errorOf(raw: unknown): ApiError {
  const e = asRecord(raw) ?? {}
  const code = typeof e.code === 'string' ? e.code : 'INTERNAL'
  const status = ERROR_STATUS[code as ErrorCode] ?? 400
  return new ApiError(code, typeof e.message === 'string' ? e.message : 'The file was not stored.', status, asRecord(e.details) ?? {})
}

/**
 * D8's answer, `data: [{ok, document | error}, …]` in the order the files
 * were sent, with `meta.stored` / `meta.refused`. A single stored document in
 * the envelope (W1's answer, with `meta.duplicate_of`) reads as a batch of one,
 * so a host on an older service still gets a result.
 */
export function uploadResult(body: unknown, meta: Record<string, unknown>, names: string[]): UploadResult {
  // A single document's answer carries its parse job in `meta.job` (as `/internal`'s does); a person's, on each file's row.
  const raw: unknown[] = Array.isArray(body) ? body : [{ ok: true, document: body, duplicate_of: meta.duplicate_of, job: meta.job }]
  const results: UploadOutcome[] = raw.map((entry, i) => {
    const r = asRecord(entry) ?? {}
    const index = typeof r.index === 'number' ? r.index : i
    const named = typeof r.file_name === 'string' ? r.file_name : undefined
    if (r.ok === false || (r.error && !r.document)) {
      return { ok: false, index, file_name: named ?? names[index] ?? '', error: errorOf(r.error) }
    }
    const document = (asRecord(r.document) ?? r) as unknown as ProDocDocument
    const job = asRecord(r.job)
    const saved = savedOfAnswer(r, document, meta, raw.length === 1)
    const passwordError = typeof r.password_error === 'string' ? r.password_error : raw.length === 1 && typeof meta.password_error === 'string' ? meta.password_error : null
    return {
      ok: true,
      index,
      file_name: named ?? names[index] ?? document.file_name,
      document,
      duplicate_of: idList(r.duplicate_of),
      job: job ? jobOf(job) : null,
      ...(saved !== undefined ? { password_saved: saved } : {}),
      ...(passwordError ? { password_error: passwordError } : {}),
    }
  })
  const stored = results.filter((r) => r.ok).length
  return {
    results,
    stored: typeof meta.stored === 'number' ? meta.stored : stored,
    refused: typeof meta.refused === 'number' ? meta.refused : results.length - stored,
  }
}

/**
 * W30 (D100): whether an upload's password was kept — the row's own
 * `password_saved`, its `version`'s, the document's (`passwordSavedOf`), or a
 * single file's `meta`. `undefined` when nothing says.
 */
function savedOfAnswer(r: Record<string, unknown>, document: ProDocDocument, meta: Record<string, unknown>, single: boolean): boolean | undefined {
  for (const value of [r.password_saved, asRecord(r.version)?.password_saved, document.password_saved, document.current_version?.password_saved, single ? meta.password_saved : undefined]) {
    if (typeof value === 'boolean') return value
  }
  if (document.password === 'saved') return true
  return undefined
}

/**
 * W30 (FR-67): whether the current version's password is saved, sealed — from
 * the document's `password_saved`, its `password: saved`, or its current
 * version's own `password_saved`, whichever the answer carries. Never a guess:
 * nothing said is `false`.
 */
export function passwordSavedOf(doc: Pick<ProDocDocument, 'password' | 'password_saved' | 'current_version'> | null | undefined): boolean {
  if (!doc) return false
  return doc.password_saved === true || doc.password === 'saved' || doc.current_version?.password_saved === true
}

/** `meta.people` (or `meta.users`): user id → display name, where the service could put one. */
function peopleOf(meta: Record<string, unknown>): Record<string, string | null> | undefined {
  const raw = asRecord(meta.people) ?? asRecord(meta.users)
  if (!raw) return undefined
  const out: Record<string, string | null> = {}
  for (const [userId, value] of Object.entries(raw)) {
    const named = typeof value === 'string' ? value : asRecord(value)?.display_name ?? asRecord(value)?.name
    out[userId] = typeof named === 'string' && named ? named : null
  }
  return out
}

const RELATION_ALIASES: Record<string, RelationKind> = {
  source_ref: 'source_ref',
  same_source_ref: 'source_ref',
  sha256: 'sha256',
  same_sha256: 'sha256',
  same_bytes: 'sha256',
  duplicate: 'sha256',
  link: 'link',
  links: 'link',
  linked: 'link',
  explicit: 'link',
}
const RELATION_ORDER: RelationKind[] = ['source_ref', 'sha256', 'link']

/**
 * `GET /documents/{id}/related` as one list, each document once with every
 * reason it is related. Read whether the service answers a list of
 * `{document, relations}` (or flat rows carrying `relation(s)` / `kind(s)`), or
 * the three groups keyed by kind.
 */
export function relatedOf(body: unknown): RelatedDocument[] {
  const found = new Map<string, RelatedDocument>()
  const add = (doc: unknown, kinds: unknown) => {
    const document = asRecord(doc) as ProDocDocument | null
    if (!document?.id) return
    const list = (Array.isArray(kinds) ? kinds : [kinds])
      .map((k) => (typeof k === 'string' ? RELATION_ALIASES[k] : undefined))
      .filter((k): k is RelationKind => Boolean(k))
    const entry = found.get(document.id) ?? { document, relations: [] }
    for (const k of list) if (!entry.relations.includes(k)) entry.relations.push(k)
    found.set(document.id, entry)
  }
  if (Array.isArray(body)) {
    for (const raw of body) {
      const row = asRecord(raw)
      if (!row) continue
      const kinds = row.relations ?? row.relation ?? row.kinds ?? row.kind ?? row.reasons ?? row.reason
      add(asRecord(row.document) ?? row, kinds)
    }
  } else {
    for (const [key, group] of Object.entries(asRecord(body) ?? {})) {
      if (Array.isArray(group)) for (const raw of group) add(asRecord(asRecord(raw)?.document) ?? raw, key)
    }
  }
  return [...found.values()].map((r) => ({ ...r, relations: RELATION_ORDER.filter((k) => r.relations.includes(k)) }))
}

/**
 * `GET /folders/{id}` as one record: the folder (flat, or under `folder`), its
 * breadcrumb (`breadcrumb`, or `ancestors` with the folder itself added) and
 * its counts (flat, or under `counts`).
 */
export function folderDetailOf(body: unknown): FolderDetail {
  const outer = asRecord(body) ?? {}
  const folder = asRecord(outer.folder) ?? outer
  const counts = asRecord(outer.counts) ?? asRecord(folder.counts) ?? {}
  const merged = { ...folder } as Record<string, unknown>
  const aliases = { document_count: ['documents'], child_count: ['children', 'folders', 'folder_count'] } as const
  for (const key of ['document_count', 'child_count'] as const) {
    const n = [folder[key], counts[key], ...aliases[key].map((a) => counts[a] ?? folder[a])].find((v) => typeof v === 'number')
    if (typeof n === 'number') merged[key] = n
  }
  const toCrumb = (raw: unknown): FolderCrumb | null => {
    const r = asRecord(raw)
    return r && typeof r.id === 'string' ? { id: r.id, name: typeof r.name === 'string' ? r.name : '' } : null
  }
  const list = (raw: unknown) => (Array.isArray(raw) ? raw.map(toCrumb).filter((c): c is FolderCrumb => Boolean(c)) : null)
  const crumbs = list(outer.breadcrumb ?? folder.breadcrumb)
  const ancestors = list(outer.ancestors ?? folder.ancestors) ?? []
  const self = typeof folder.id === 'string' ? [{ id: folder.id, name: typeof folder.name === 'string' ? folder.name : '' }] : []
  // A breadcrumb ends at the folder itself, whether or not the service counted it in.
  const trail = crumbs ?? ancestors
  const ends = trail.length > 0 && trail[trail.length - 1]!.id === self[0]?.id
  return { ...(merged as unknown as Folder), breadcrumb: ends ? trail : [...trail, ...self] }
}

/** `POST /folders/paths`'s map, from `{"a/b": id}`, `{paths: {…}}` or a list of `{path, id}`. */
export function pathsOf(body: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  const record = asRecord(body)
  const nested = record ? asRecord(record.paths) ?? asRecord(record.folders) : null
  if (Array.isArray(body) || Array.isArray(record?.paths)) {
    for (const raw of (Array.isArray(body) ? body : (record!.paths as unknown[])) ?? []) {
      const r = asRecord(raw)
      const path = Array.isArray(r?.path) ? (r!.path as unknown[]).join('/') : r?.path
      const folderId = r?.id ?? r?.folder_id
      if (typeof path === 'string' && typeof folderId === 'string') out[path] = folderId
    }
    return out
  }
  for (const [path, value] of Object.entries(nested ?? record ?? {})) {
    const folderId = typeof value === 'string' ? value : asRecord(value)?.id
    if (typeof folderId === 'string') out[path] = folderId
  }
  return out
}

/**
 * The bulk answer, per document in the order they were sent: `data: [{document_id
 * | id, ok, document? | error?}]` with `meta.changed` / `meta.refused`, or
 * `data: {results: […]}`.
 */
export function bulkResult(body: unknown, meta: Record<string, unknown>, ids: string[]): BulkResult {
  const raw = Array.isArray(body) ? body : Array.isArray(asRecord(body)?.results) ? (asRecord(body)!.results as unknown[]) : []
  const results: BulkOutcome[] = raw.map((entry, i) => {
    const r = asRecord(entry) ?? {}
    const documentId = typeof r.document_id === 'string' ? r.document_id : typeof r.id === 'string' ? r.id : (ids[i] ?? '')
    if (r.ok === false || (r.error && r.ok !== true)) return { ok: false, document_id: documentId, error: errorOf(r.error) }
    const document = asRecord(r.document) as ProDocDocument | null
    return { ok: true, document_id: documentId, ...(document ? { document } : {}) }
  })
  const changed = results.filter((r) => r.ok).length
  const summary = asRecord(body)
  const count = (key: string, fallback: number) => {
    const n = meta[key] ?? summary?.[key]
    return typeof n === 'number' ? n : fallback
  }
  return { results, changed: count('changed', changed), refused: count('refused', results.length - changed) }
}

/**
 * `GET /summary`, with every field present — a count the service did not send
 * is `0`, a list `[]`, an unset limit `null`, an unsaid period `null`. W22's
 * `daily` keeps only well-formed days, oldest first as the service sends them.
 */
export function summaryOf(body: unknown, meta: Record<string, unknown> = {}): Summary {
  const r = asRecord(body) ?? {}
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
  const pipeline = asRecord(r.pipeline) ?? {}
  const period = asRecord(meta.period)
  const from = str(period?.from)
  const to = str(period?.to)
  const day = (raw: unknown): SummaryDay | null => {
    const d = asRecord(raw)
    const date = str(d?.date)
    return d && date ? { date, uploaded: n(d.uploaded), approved: n(d.approved), pending: n(d.pending) } : null
  }
  const counts = (v: unknown): Record<string, number> => {
    if (Array.isArray(v)) {
      // `[{value | id | workspace_id | document_type, count}]` as well as a map.
      const out: Record<string, number> = {}
      for (const raw of v) {
        const row = asRecord(raw) ?? {}
        const key = row.value ?? row.id ?? row.workspace_id ?? row.document_type ?? row.key
        if (typeof key === 'string') out[key] = n(row.count)
      }
      return out
    }
    return Object.fromEntries(Object.entries(asRecord(v) ?? {}).map(([k, c]) => [k, n(c)]))
  }
  const storage = asRecord(r.storage) ?? {}
  // The service sends the cap as `cap_bytes` (app-mgmt's `storage` answer).
  const limit = storage.cap_bytes ?? storage.limit_bytes ?? storage.limit
  const activity = Array.isArray(r.recent_activity) ? r.recent_activity : []
  // The service nests the three counts under `documents`; a flat shape is read too.
  const docs = asRecord(r.documents) ?? r
  return {
    total: n(docs.total),
    awaiting_review: n(docs.awaiting_review),
    uploaded_this_month: n(docs.uploaded_this_month),
    by_workspace: counts(r.by_workspace),
    by_type: counts(r.by_type),
    recent_uploads: (Array.isArray(r.recent_uploads) ? r.recent_uploads : []) as ProDocDocument[],
    recent_activity: activity.map((raw, i) => {
      const a = asRecord(raw) ?? {}
      const actor = (asRecord(a.actor) ?? {}) as Partial<AuditActor>
      return {
        id: typeof a.id === 'string' ? a.id : `activity-${i}`,
        occurred_at: typeof a.occurred_at === 'string' ? a.occurred_at : '',
        action: typeof a.action === 'string' ? a.action : null,
        document_id: typeof a.document_id === 'string' ? a.document_id : null,
        document_title: typeof a.document_title === 'string' ? a.document_title : typeof a.title === 'string' ? a.title : null,
        actor: { kind: actor.kind ?? 'user', user_id: actor.user_id ?? null, service: actor.service ?? null, display_name: actor.display_name ?? null },
      }
    }),
    storage: { used_bytes: n(storage.used_bytes ?? storage.used), limit_bytes: typeof limit === 'number' ? limit : null },
    extractions_needs_review: n(asRecord(r.extractions)?.needs_review ?? r.extractions_needs_review),
    pipeline: {
      uploaded: n(pipeline.uploaded),
      reading: n(pipeline.reading),
      needs_review: n(pipeline.needs_review),
      accepted: n(pipeline.accepted),
      failed: n(pipeline.failed),
    },
    daily: (Array.isArray(r.daily) ? r.daily : []).map(day).filter((d): d is SummaryDay => d !== null),
    period: from && to ? { from, to } : null,
  }
}

/** The name `Content-Disposition` carries — never the key's last segment (FR-10). */
export function fileNameOf(res: Response): string | null {
  const header = res.headers.get('content-disposition') ?? ''
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header)
  const plain = /filename="([^"]+)"/i.exec(header)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1])
    } catch {
      return star[1]
    }
  }
  return plain?.[1] ?? null
}

/** The `details.document_count` of a workspace-archive refusal: how many documents need somewhere to go. */
export function documentsToMove(e: unknown): number | null {
  if (!(e instanceof ApiError) || e.code !== 'CONFLICT') return null
  const n = e.details.document_count
  return typeof n === 'number' ? n : null
}

/* ------------------------------------------------------------ who acted */

/**
 * The apps a person meets by name — spec FR-33's pairs, named as
 * `app/integration_pairs.py` names them — by the slug a document carries as
 * `owner_app` or by the credential an app's row carries as `actor_service`
 * (`prodesk-service`). Anything else reads as it came, never a guess.
 */
const APP_NAMES: Record<string, string> = {
  prodoc: 'ProDoc',
  'ca-erp': 'ProDesk',
  prodesk: 'ProDesk',
  gst: 'ProGST',
  progst: 'ProGST',
  itax: 'iTax',
  // W31 (FR-69): TDS Reco's slug, and its credentials' stem (`tdsreco-service`, `tdsreco-worker`).
  'tds-reco': 'TDS Reco',
  tdsreco: 'TDS Reco',
}

/** `ca-erp` and `prodesk-service` are "ProDesk"; an app not named above reads as it came. */
export function appName(app: string): string {
  const name = app.trim().toLowerCase()
  return APP_NAMES[name] ?? APP_NAMES[name.replace(/-(service|worker|web)$/, '')] ?? app
}

/**
 * A party's kind as a reader says it (FR-40). ProDesk's `person:<id>` is a
 * client's contact (ADR-0014 §2). Only the kind is ever said: the id means
 * nothing to a reader, and nothing else can ride the header.
 */
const PARTY_KINDS: Record<string, string> = { person: 'a client contact' }

export function partyLabel(party: string): string {
  return PARTY_KINDS[party.split(':', 1)[0] ?? ''] ?? 'someone outside the organisation'
}

/**
 * Whom an app acted for (FR-40), as a line says it: "ProDesk for you",
 * "ProDesk for Asha Rao" — "for another member" while the identity projection
 * has no name — "ProDesk, for a client contact", or "ProDesk" alone. The app
 * is always said: the person is the app's attestation, recorded beside it and
 * never instead of it.
 */
export function actedForLabel(
  acted: { service: string | null; user_id: string | null; party?: string | null; display_name?: string | null },
  userId: string | null | undefined,
): string {
  const app = acted.service ? appName(acted.service) : 'An app'
  if (acted.user_id) return `${app} for ${userId && acted.user_id === userId ? 'you' : (acted.display_name ?? 'another member')}`
  if (acted.party) return `${app}, for ${partyLabel(acted.party)}`
  return app
}

/* ------------------------------------------------------------- the rules */

type Can = (resource: string, verb: Verb) => boolean

type UploaderFacts = Pick<ProDocDocument, 'uploaded_by' | 'current_version' | 'current_version_uploaded_by'>

/**
 * Who uploaded the **current** version: the version itself when the screen has
 * it, else the record's `current_version_uploaded_by` (W4) — where `null` means
 * a sibling app's version, which no person uploaded — and only from a service
 * older than W4 the first version's uploader.
 */
export function currentUploaderOf(doc: UploaderFacts): string | null {
  if (doc.current_version) return doc.current_version.uploaded_by
  if (doc.current_version_uploaded_by !== undefined) return doc.current_version_uploaded_by
  return doc.uploaded_by
}

/**
 * The browser's mirror of the service's `may_edit` (D12): `can_update` on
 * `document` edits any visible document; `can_create` edits only a document
 * whose **current version** the caller uploaded, while it is not approved.
 * An archived document is edited by nobody. The service decides; this only
 * keeps a button from offering what will be refused.
 */
export function mayEdit(can: Can, doc: Pick<ProDocDocument, 'status' | 'review_state'> & UploaderFacts, userId: string | null | undefined): boolean {
  if (doc.status !== 'stored') return false
  if (can(DOCUMENT, 'can_update')) return true
  if (!can(DOCUMENT, 'can_create') || !userId) return false
  return currentUploaderOf(doc) === userId && doc.review_state !== 'approved'
}

/** Restore is the archive authority over the whole organisation (D11): `can_delete` **and** `can_view_all`. */
export function mayRestore(can: Can): boolean {
  return can(DOCUMENT, 'can_delete') && can(DOCUMENT, 'can_view_all')
}

/**
 * Approve and reject are `can_update` on `document` **alone** (D12, plan
 * §5.2) — held by `reviewer` and `admin`, never by `uploader`, whose own-upload
 * edits ride `can_create`. Four-eyes narrows it further per document; the
 * service decides that, and `isOwnVersion` only lets a screen say so first.
 */
export function mayReview(can: Can): boolean {
  return can(DOCUMENT, 'can_update')
}

/**
 * *Request review* (plan §5.2: own+create) moves an `uploaded` document to
 * `needs_review` — offered to whoever may edit it (D12's `mayEdit`), and only
 * from `uploaded`: a document already waiting, approved or rejected moves on by
 * a decision or a new version, never by asking again (D14, no reopen).
 */
export function mayRequestReview(can: Can, doc: Pick<ProDocDocument, 'status' | 'review_state'> & UploaderFacts, userId: string | null | undefined): boolean {
  return (doc.review_state ?? 'uploaded') === 'uploaded' && mayEdit(can, doc, userId)
}

/**
 * What a selection may be asked to do (FR-16), by the verbs held — the bar
 * offers nothing the service would refuse every time. Tagging and moving are
 * D12's own+create, so `can_create` on `document` offers them and the service
 * answers per document; archiving is `can_delete`; the ZIP is a read.
 */
export function bulkVerbs(can: Can): { tag: boolean; move: boolean; archive: boolean; zip: boolean } {
  const edit = can(DOCUMENT, 'can_update') || can(DOCUMENT, 'can_create')
  return {
    tag: edit && can(TAG, 'can_view'),
    move: edit,
    archive: can(DOCUMENT, 'can_delete'),
    zip: can(DOCUMENT, 'can_view'),
  }
}

/**
 * W6's verbs, as the service gates them (plan §5.2). Sharing is `can_create`
 * on `share`; a link for **anyone** adds step-up, an **app** share
 * `settings:can_update` (D26). A share is revoked by its creator or by a
 * holder of `share:can_delete`. A retention override and an erasure are the
 * settings administrator's (`settings:can_update`), each behind step-up.
 */
export function mayShare(can: Can): boolean {
  return can(SHARE, 'can_create')
}

export function mayShareToApp(can: Can): boolean {
  return can(SHARE, 'can_create') && can(SETTINGS, 'can_update')
}

export function mayRevokeShare(can: Can, share: Pick<Share, 'created_by' | 'revoked_at'>, userId: string | null | undefined): boolean {
  if (share.revoked_at) return false
  return can(SHARE, 'can_delete') || (Boolean(userId) && share.created_by === userId)
}

export function mayOverrideRetention(can: Can): boolean {
  return can(SETTINGS, 'can_update')
}

export function mayErase(can: Can): boolean {
  return can(SETTINGS, 'can_update')
}

/**
 * Whether people may change a folder's **structure** here — rename, move,
 * archive (§9-19): only a folder this app made. A sibling app's folder answers
 * `APP_OWNED`; documents are still filed into it. `app` is the service's own
 * slug (`/documents/options`'s `app`).
 */
export function isOwnFolder(folder: Pick<Folder, 'owner_app'>, app = 'prodoc'): boolean {
  return folder.owner_app === app
}

/**
 * Whether the person uploaded the version a decision would judge — the one
 * four-eyes asks about (FR-23: "the uploader of the version under review").
 * A sibling app's version has no person behind it, so it is nobody's own. The
 * service's answer (`FOUR_EYES`) is the authority.
 */
export function isOwnVersion(doc: UploaderFacts, userId: string | null | undefined): boolean {
  return Boolean(userId) && currentUploaderOf(doc) === userId
}

/** A document type's label from the served list, or the code said plainly. */
export function documentTypeLabel(code: string | null | undefined, types: DocumentTypeOption[] | null | undefined): string {
  if (!code) return 'Other'
  const found = types?.find((t) => t.code === code)
  if (found) return found.label
  const words = code.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The file's SHA-256 as hex, computed in the browser (`crypto.subtle`) so the
 * uploader can ask `/documents/duplicates` **before** sending — a courtesy
 * (D7): the service re-hashes the bytes it receives and never trusts this one.
 */
export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(await bytesOf(blob)))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/** A blob's bytes — `Blob.arrayBuffer()` where the platform has it, `FileReader` where it does not. */
export function bytesOf(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'))
    reader.readAsArrayBuffer(blob)
  })
}

/* ------------------------------------------------------------ vocabulary */

/** The Word type's long name, written once. */
export const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/** JSON (W10, FR-44) — an ITD return, a portal AIS / 26AS download. Stored and versioned like any file; never previewed. */
export const JSON_MEDIA_TYPE = 'application/json'

/** Plain text (engine W18, FR-47) — a Form 26AS's TRACES text export. Stored and versioned like any file; never previewed. */
export const TEXT_MEDIA_TYPE = 'text/plain'

/** An e-mail (W31, FR-68) — an `.eml`. Stored, versioned and downloaded; never opened (ADR-0022). */
export const EML_MEDIA_TYPE = 'message/rfc822'

/** An Outlook message (W31, FR-68) — an `.msg`. Stored, versioned and downloaded; never opened. */
export const MSG_MEDIA_TYPE = 'application/vnd.ms-outlook'

/** A ZIP archive (W31, FR-68). Stored, versioned and downloaded; never unpacked — no member is listed or previewed. */
export const ZIP_MEDIA_TYPE = 'application/zip'

/** The three kinds ProDoc keeps and never opens (ADR-0022): no preview, no scan, no reading. */
export const NEVER_OPENED_MEDIA_TYPES: readonly string[] = [EML_MEDIA_TYPE, MSG_MEDIA_TYPE, ZIP_MEDIA_TYPE]

/**
 * Media type → the extensions a file picker accepts, for the `accept`
 * attribute. The *list* of accepted types comes from the service; this is
 * only how a browser spells each one. The service decides the type by the
 * bytes' signature, whatever the extension says (FR-2).
 */
const EXTENSIONS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/tiff': ['.tif', '.tiff'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  [DOCX]: ['.docx'],
  [JSON_MEDIA_TYPE]: ['.json'],
  [TEXT_MEDIA_TYPE]: ['.txt'],
  [EML_MEDIA_TYPE]: ['.eml'],
  [MSG_MEDIA_TYPE]: ['.msg'],
  [ZIP_MEDIA_TYPE]: ['.zip'],
}

export function acceptFor(mediaTypes: string[]): string {
  return mediaTypes.flatMap((type) => EXTENSIONS[type] ?? []).join(',')
}

/** Which of the served media types a file is, by its name — `null` for one the org does not accept. */
export function mediaTypeOf(fileName: string, mediaTypes: string[]): string | null {
  const ext = `.${fileName.split('.').pop()?.toLowerCase() ?? ''}`
  return mediaTypes.find((type) => (EXTENSIONS[type] ?? []).includes(ext)) ?? null
}

/** The kind of file, for an icon: `pdf`, `image`, `sheet`, `text` (a Word document) or `file`. */
export type FileKind = 'pdf' | 'image' | 'sheet' | 'text' | 'file'

export function fileKindOf(mediaType: string | null | undefined): FileKind {
  if (!mediaType) return 'file'
  if (mediaType === 'application/pdf') return 'pdf'
  if (mediaType.startsWith('image/')) return 'image'
  if (mediaType === 'text/csv' || mediaType.includes('spreadsheet') || mediaType.includes('ms-excel')) return 'sheet'
  if (mediaType === DOCX) return 'text'
  return 'file'
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
